/* ==========================================================================
   Sound Event Detector - Complete Multi-Language System (i18n)
   Professional Multilingual Translations across EN, CS, DE, ES, AR, PT
   ========================================================================== */

export const locales = {
  en: {
    name: 'English',
    dir: 'ltr',
    translations: {
      nav: {
        solutions: 'Solutions',
        detection: 'Detection',
        markets: 'Markets',
        cooperation: 'Cooperation',
        distributorsMap: 'Distributors & System Integrators Map',
        integratorsMap: 'Distributors & System Integrators Map',
        news: 'News',
        latestNews: 'Latest News & Press',
        caseStudies: 'Case Studies',
        productUpdates: 'Product & Firmware Updates',
        aboutUs: 'About Us',
        manuals: 'Manuals & Datasheets',
        contact: 'Contact',
        getQuote: 'Request Quote / Demo',
        privacy: 'Privacy Policy',
        privacyPolicy: 'Privacy Policy',
        terms: 'Terms & Conditions',
        codeOfEthics: 'Code of Ethics',
        policyIMS: 'IMS Security Policy',
        cookiePolicy: 'Cookie Policy'
      },
      hero: {
        subtitle: 'Detect gunshots, screams, aggression, and breaking glass in real time. Deploy as standalone edge hardware or native smart camera apps without streaming continuous audio.',
        btnSolutions: 'Explore Solutions & Architecture',
        btnContact: 'Request Free Consultation'
      },
      common: {
        learnMore: 'Learn More →',
        readMore: 'Read Full Report →',
        contactUs: 'Contact Sales & Engineering →',
        applyContact: 'Contact Us for Partnership →',
        allTypes: 'Explore All Cooperation Models →',
        submitApp: 'Submit Application →',
        keyBenefits: 'Key Partnership Benefits',
        backToHome: 'Back to Home',
        search: 'Search...',
        loading: 'Loading...',
        downloadPdf: 'Download Official PDF',
        viewDetails: 'View Details',
        close: 'Close',
        publishedOn: 'Published on',
        allCategories: 'All Categories',
        hardwareVersion: 'Hardware Version',
        cameraVersion: 'Camera App Version'
      },
      customers: {
        title: 'Proven Field Deployments',
        subtitle: 'Trusted by municipalities, police departments, healthcare facilities, and infrastructure operators across Europe.'
      },
      testimonials: {
        title: 'What Security Leaders Say About SED',
        subtitle: 'Direct feedback from municipal mayors, hospital security directors, and global technology partners.'
      },
      newsSection: {
        title: 'Latest News & Press Releases',
        subtitle: 'Stay updated with the latest technological developments, TV reports, awards, and deployment announcements.'
      },
      marketTypes: {
        publicSafety: 'Cities & Public Safety',
        public: 'Cities & Public Safety',
        healthcare: 'Healthcare & Hospitals',
        education: 'Education & Campuses',
        industrial: 'Industry & Critical Infrastructure',
        private: 'Private Property & Commercial Sites'
      },
      detectionTypes: {
        gunshot: 'Gunshot & Explosions',
        glass: 'Glass Break Detection',
        drone: 'Drone & UAV Perimeter',
        scream: 'Scream & Aggression',
        graffiti: 'Graffiti & Spray Vandalism',
        grinder: 'Angle Grinder & Cutting',
        siren: 'Emergency Vehicle Siren',
        other: 'Custom & Industrial Audio'
      },
      detectionPages: {
        gunshot: {
          title: 'Real-Time Gunshot Detection',
          lead: 'Instant detection of firearm discharges in under 5s with PTZ camera auto-slewing and dispatch alerting.',
          desc: 'Identifies firearm acoustic impulse shockwaves while eliminating false alarms.'
        },
        glass: {
          title: 'Acoustic Glass Break Detection',
          lead: 'Real-time acoustic AI identification of glass fractures and window shattering for burglary prevention.',
          desc: 'Detects dual-frequency impact and high-pitch resonance before perimeter entry.'
        },
        drone: {
          title: 'Drone & UAV Perimeter Protection',
          lead: '360° acoustic tracking of drone rotor harmonics for critical infrastructure and prisons.',
          desc: 'Detects unauthorized drones at long range in any weather condition.'
        },
        scream: {
          title: 'Scream & Aggression Classification',
          lead: 'Real-time classification of panic screams and hostile aggression for de-escalation.',
          desc: '100% GDPR compliant acoustic AI without voice wiretapping.'
        },
        graffiti: {
          title: 'Graffiti & Vandalism Prevention',
          lead: 'Detects aerosol nozzle pressure hiss and spray can agitators to protect public property.',
          desc: 'Stops vandals before significant defacement occurs.'
        },
        grinder: {
          title: 'Angle Grinder & Cutting Tool Detection',
          lead: 'Real-time detection of high-RPM metal cutting for ATM vault and perimeter protection.',
          desc: 'Alerts security teams immediately during fence and safe breaches.'
        },
        siren: {
          title: 'Emergency Siren Acoustic Recognition',
          lead: 'Classifies emergency vehicle sirens for intelligent traffic priority and green waves.',
          desc: 'Accelerates emergency response vehicle transit times across cities.'
        },
        other: {
          title: 'Custom & Industrial Acoustic AI',
          lead: 'Tailored acoustic classifiers for industrial machine monitoring and custom sounds.',
          desc: 'Bespoke neural classifiers trained on specialized customer audio signatures.'
        }
      },
      footer: {
        company: 'Company',
        legalHeading: 'Legal & Compliance',
        tagline: 'Adding a digital acoustic ear to existing security camera infrastructure.',
        trademark: 'Sound Event Detector is a registered trademark of JALUD Embedded s.r.o. (EUIPO No. 018598475)',
        certifications: 'ISO 9001:2015 & ISO 27001 Certified · 100% GDPR Compliant',
        legal: 'All Rights Reserved',
        copyright: '© 2020–2026 JALUD Embedded s.r.o. All rights reserved.'
      },
      home: {
        heroBadge: '🏆 European Leader in AI Acoustic Event Detection · Edge Embedded AI',
        heroTitle: 'Audio Intelligence for Instant Security Response',
        heroTitleLine1: 'The camera sees.',
        heroTitleLine2: 'SED hears.',
        heroSubtitle: 'Detect gunshots, screams, aggression, and breaking glass in real time. Deploy as standalone edge hardware or native smart camera apps without streaming continuous audio.',
        requestConsultation: 'Request Free Consultation',
        viewDetections: 'Explore Detection Capabilities →',
        exploreSolutions: 'Explore Solutions & Architecture',
        videoEyebrow: 'REAL-WORLD DEMONSTRATIONS',
        seeInAction: 'See Sound Event Detector in Action',
        watchFieldDemo: 'Watch real-time audio event detection, automated PTZ camera tracking, and municipal security integration in practice.',
        videoCtTitle: 'Czech Television (ČT24) - Bilance Feature ↗',
        videoCtDesc: 'Special audio detectors in Pilsen sent 18 police patrols to the field in January alone after detecting panic screams and glass break incidents.',
        videoCtTag: '📺 ČT24 Bilance',
        videoGunTitle: 'Gunshot Detection & PTZ Camera Auto-Tracking ↗',
        videoGunDesc: 'Tactical field demonstration showing SED detecting live gunshot blasts and auto-slewing municipal cameras to sound coordinates.',
        videoGunTag: '💥 Tactical Demo',
        watchYt: 'Watch on YouTube ↗',
        watchFieldDemo: 'Watch Field & Police Video Demonstrations',
        statResponse: '7x Faster Response',
        statResponseDesc: 'Acoustic detection notifies dispatchers in <5s, long before callers reach emergency lines.',
        statCameras: '0 New Cameras Required',
        statCamerasDesc: 'Integrates natively via ONVIF and VMS plugins with your existing Axis, Hanwha, or Milestone infrastructure.',
        statPrivacy: '100% Privacy Shield',
        statPrivacyDesc: 'Zero continuous audio recording. Neural networks process sound exclusively in volatile edge RAM.',
        acousticAdvantageEyebrow: 'ACOUSTIC ADVANTAGE',
        comprehensiveCoverageEyebrow: 'COMPREHENSIVE ACOUSTIC COVERAGE',
        flexibleArchEyebrow: 'FLEXIBLE ARCHITECTURE',
        consultEyebrow: 'DIRECT ENGINEERING CONSULTATION',
        customerEyebrow: 'PROVEN FIELD DEPLOYMENTS',
        meetTheTeam: 'MEET THE LEADERSHIP TEAM',
        docsResources: 'DOCUMENTATION & RESOURCES',
        cooperationEyebrow: 'PARTNERSHIP & COLLABORATION',
        blindSpotsTitle: 'Cameras Have Blind Spots. Sound Does Not.',
        blindSpotsLead: 'Traditional video surveillance is limited by field-of-view, lighting conditions, corners, and foliage. SED turns your security system into a responsive 360-degree acoustic radar.',
        threatsTitle: 'Detect Security Threats Before They Escalate',
        threatsSubtitle: 'Advanced neural networks trained on hundreds of thousands of real acoustic impulse profiles.',
        liveAlertsTitle: 'Live Real-Time Event Stream (Pilsen Smart City Deployment)',
        consultTitle: 'Request Free Technical Consultation & Acoustic Site Assessment',
        consultDesc: 'Our engineering team will assess your facility requirements and recommend the optimal hardware or camera edge app configuration.',
        nameLabel: 'Full Name *',
        companyLabel: 'Company or Municipality *',
        emailLabel: 'Work Email *',
        facilityLabel: 'Facility Description & Requirements *',
        facilityPlaceholder: 'Describe your facility & requirements (e.g. 2,000m² warehouse, IP cameras (Axis, Hanwha, Hikvision), need panic scream alert)...',
        namePlaceholder: 'John Doe',
        companyPlaceholder: 'e.g. City of Pilsen, Securitas',
        emailPlaceholder: 'john.doe@organization.com',
        submitConsult: 'Submit Consultation Request →',
        consultNotice: 'Inquiries are delivered directly to sales@jalud-embedded.com. Non-binding consultation.',
        consultSuccess: 'Consultation Request Submitted! Our team will review your requirements and respond within 1 business day.',
        consultFailed: 'Could not submit request. Please write us directly at sales@jalud-embedded.com.'
      },
      solutions: {
        eyebrow: 'DEPLOYMENT OPTIONS & SYSTEM ARCHITECTURE',
        title: 'Three Flexible Ways to Deploy SED Acoustic Intelligence',
        subtitle: 'From standalone edge IoT sensors to native embedded smart camera applications and centralized enterprise servers.',
        tier1Title: '1. Edge Hardware & Smart Camera Apps',
        tier1Desc: 'Runs locally on Axis ACAP, Hanwha Open Platform, or dedicated SED-Box hardware. Detects sound events in volatile RAM with zero audio wiretapping.',
        tier2Title: '2. Centralized Server & Cloud Cluster',
        tier2Desc: 'Aggregates hundreds of distributed acoustic sensors across entire metropolitan areas, industrial sites, and campus perimeters.',
        tier3Title: '3. SED Mobile App & Rapid Alerting',
        tier3Desc: 'Delivers sub-second push notifications with GPS coordinates and sound spectrograms directly to security guards and police patrol officers.',
        vmsTitle: 'Supported Video Management Systems (VMS) & Ecosystem',
        vmsSubtitle: 'Seamless plug-and-play integration with world-leading security platforms via ONVIF Profile M, REST API, and native plugins.',
        downloadSpecs: 'Download System Architecture Specification (PDF) →'
      },
      aboutUs: {
        eyebrow: 'ABOUT JALUD EMBEDDED',
        title: 'Pioneering European Edge Acoustic AI',
        subtitle: 'Founded by acoustic engineers and computer vision scientists in Pilsen, Czech Republic. Dedicated to making public spaces and workplaces safer without compromising citizen privacy.',
        teamTitle: 'Leadership & R&D Engineering Team',
        storyTitle: 'Our Mission & Technological Journey',
        storyP1: 'JALUD Embedded s.r.o. was established with a singular focus: transforming sound analysis from expensive proprietary laboratory hardware into accessible, lightweight edge intelligence.',
        storyP2: 'Our neural classifiers have won the National AI Award, the Czech Visionaries Award, and the European Seal of Excellence, protecting thousands of citizens across European smart cities.',
        awardsTitle: 'Awards, Certifications & Standards',
        iso9001: 'ISO 9001:2015 Quality Management Certified',
        iso27001: 'ISO/IEC 27001 Information Security Certified',
        gdprCertified: '100% GDPR Compliant — Confirmed by Legal Opinion & Personal Data Office',
        ceMark: 'CE & FCC Certified Hardware Products'
      },
      mapPage: {
        eyebrow: 'GLOBAL NETWORK & PARTNERS',
        title: 'Global Distributors & System Integrators Map',
        subtitle: 'Explore our certified distributors, authorized system integrators, and technology partners deploying SED acoustic intelligence worldwide.',
        filterType: 'Filter Partner Type:',
        allPartners: 'All Partners',
        distributorsOnly: 'Distributors Only',
        integratorsOnly: 'Integrators & Partners Only',
        regionalZoom: 'Regional Zoom:',
        mapTitle: 'Interactive Zoomable Partner Map',
        mapHint: 'Scroll or click + / - to zoom into street level',
        legendDistributor: 'Blue Pin = Master Distributor',
        legendIntegrator: 'Amber Pin = System Integrator',
        loadingMap: 'Loading High-Contrast Street Tiles & Pins...',
        directoryTitle: 'Directory Listing',
        searchPlaceholder: '🔍 Search company name, city, email or country...',
        noMatches: 'No Partners Found',
        noMatchesHint: 'Try clearing your search query or selecting a different partner filter.',
        zoomOnMap: 'Zoom on Map',
        ctaTitle: 'Become an Authorized SED Distributor or System Integrator',
        ctaDesc: "Expand your security portfolio with the world's leading edge AI acoustic detection technology. Native edge plugins across all supported platforms (Axis, Hanwha, Hikvision...), VMS integrations, zero-cost NFR (not for resell) SW licenses for testing and demonstrations, and partner marketing support provided.",
        ctaButton: 'Apply for Partner Program →'
      },
      contactPage: {
        subtitle: 'Have questions about Sound Event Detector, camera integrations, or partner cooperation? Our engineering and sales team is here to assist you.',
        placeholder: 'How can we assist you with acoustic event detection or deployment?',
        inquiryNotice: 'Inquiries are delivered directly to sales@jalud-embedded.com.',
        businessHours: 'Business Hours: Mon - Fri, 8:00 - 17:00 CET',
        hqTitle: 'JALUD Embedded s.r.o. Headquarters',
        devTitle: 'JALUD Embedded s.r.o. Development',
        supportTitle: 'Global Support & Compliance',
        isoCert: 'ISO 9001 · ISO 27001 · CE Certified Quality & Security',
        euTrademark: 'EU Registered Trademark No. 018598475',
        title: 'Get in Touch with Our Team',
        eyebrow: 'HEADQUARTERS & DEVELOPMENT',
        headquarters: 'Headquarters',
        development: 'R&D & Engineering',
        globalSupport: 'Global Support & Compliance',
        name: 'Full Name',
        company: 'Company / Municipality',
        email: 'Work Email Address',
        phone: 'Phone Number (Optional)',
        facility: 'Facility / Deployment Description',
        submit: 'Send Message →',
        sending: 'Sending Message...',
        successTitle: 'Message Sent Successfully!',
        successDesc: 'Thank you for reaching out! Your message has been sent to sales@jalud-embedded.com. Our team will contact you shortly.',
        errorTitle: 'Message Delivery Failed',
        errorDesc: 'Could not send message. Please check your connection or email us directly at sales@jalud-embedded.com.'
      },
      radar: {
        liveStream: 'RADAR.LIVESTREAM',
        location: 'City Pilsen',
        disclaimer: 'Data is delayed by 1 hour and event sounds are simulated.',
        unitsCount: '{count} Active Units Online',
        unitsLabel: 'Active Live Units',
        confidence: 'Confidence',
        forwarded: 'Automated VMS Forwarding Active',
        justNow: 'Just now',
        agoSec: '{sec}s ago',
        agoMin: '{min}m ago',
        events: {
          gunshot: 'Gunshot Detection',
          scream: 'Panic Scream',
          maleScream: 'Male Scream',
          childScream: 'Child Scream',
          aggression: 'Verbal Aggression',
          glass: 'Glass Break',
          drone: 'Drone Detection',
          grinder: 'Angle Grinder',
          siren: 'Vehicle Siren',
          spray: 'Spray Aerosol',
          other: 'Sound Event'
        }
      },
      cookie: {
        text: 'We use essential cookies to analyze traffic, protect web forms, and ensure optimal sound security performance.',
        decline: 'Decline Optional',
        accept: 'Accept All Cookies'
      },
      cooperationTypes: {
        citizen: 'Active Citizens & Municipalities',
        distributor: 'Distributor Program',
        integrator: 'System Integrator',
        customer: 'Customer & SLA Support',
        investor: 'Investor Relations',
        partner: 'Technology & OEM Partner'
      }
    }
  },
  cs: {
    name: 'Čeština',
    dir: 'ltr',
    translations: {
      nav: {
        solutions: 'Řešení',
        detection: 'Detekce',
        markets: 'Trhy',
        cooperation: 'Spolupráce',
        distributorsMap: 'Mapa distributorů a systémových integrátorů',
        integratorsMap: 'Mapa distributorů a systémových integrátorů',
        news: 'Novinky',
        latestNews: 'Aktuality a tisk',
        caseStudies: 'Případové studie',
        productUpdates: 'Aktualizace produktů',
        aboutUs: 'O nás',
        manuals: 'Manuály a datasheety',
        contact: 'Kontakt',
        getQuote: 'Poptávka / Demo',
        privacy: 'Ochrana soukromí',
        privacyPolicy: 'Ochrana soukromí',
        terms: 'Obchodní podmínky',
        codeOfEthics: 'Etický kodex',
        policyIMS: 'Bezpečnostní politika IMS',
        cookiePolicy: 'Zásady cookies'
      },
      hero: {
        subtitle: 'Detekujte střelbu, panický křik, agresi a rozbití skla v reálném čase. Využijte jako samostatný hardware nebo nativní aplikaci do chytrých kamer bez nahrávání konverzací.',
        btnSolutions: 'Prozkoumat řešení a architekturu',
        btnContact: 'Požádat o bezplatnou konzultaci'
      },
      common: {
        learnMore: 'Zjistit více →',
        readMore: 'Číst celou studii →',
        contactUs: 'Kontaktovat obchod a vývoj →',
        applyContact: 'Kontaktovat ohledně partnerství →',
        allTypes: 'Prohlédnout všechny modely spolupráce →',
        submitApp: 'Odeslat žádost →',
        keyBenefits: 'Klíčové výhody partnerství',
        backToHome: 'Zpět na úvod',
        search: 'Hledat...',
        loading: 'Načítání...',
        downloadPdf: 'Stáhnout oficiální PDF',
        viewDetails: 'Zobrazit podrobnosti',
        close: 'Zavřít',
        publishedOn: 'Publikováno dne',
        allCategories: 'Všechny kategorie',
        hardwareVersion: 'Hardwarová verze',
        cameraVersion: 'Kamerová aplikace'
      },
      customers: {
        title: 'Ověřená nasazení v reálném provozu',
        subtitle: 'Důvěřují nám města, městské policie, nemocnice a provozovatelé kritické infrastruktury po celé Evropě.'
      },
      testimonials: {
        title: 'Co říkají bezpečnostní experti o SED',
        subtitle: 'Zkušenosti primátorů měst, ředitelů bezpečnosti nemocnic a technologických partnerů.'
      },
      newsSection: {
        title: 'Aktuality a tiskové zprávy',
        subtitle: 'Sledujte nejnovější technologické inovace, televizní reportáže, ocenění a nasazení Sound Event Detectoru.'
      },
      marketTypes: {
        publicSafety: 'Města a veřejná bezpečnost',
        public: 'Města a veřejná bezpečnost',
        healthcare: 'Zdravotnictví',
        education: 'Školství a fakulty',
        industrial: 'Průmysl a kritická infrastruktura',
        private: 'Soukromý majetek a komerční objekty'
      },
      detectionTypes: {
        gunshot: 'Výstřely z palných zbraní',
        glass: 'Tříštění skla',
        drone: 'Detekce dronů a UAV',
        scream: 'Panický křik a agrese',
        graffiti: 'Vandalismus sprejováním graffiti',
        grinder: 'Úhlová bruska a řezání',
        siren: 'Sirény vozidel IZS',
        other: 'Zakázkové a průmyslové zvuky'
      },
      detectionPages: {
        gunshot: {
          title: 'Detekce střelby v reálném čase',
          lead: 'Okamžitá akustická identifikace výstřelů do <5s s automatickým natočením PTZ kamer a dispečerským varováním.',
          desc: 'Analyzuje akustické rázové vlny výstřelů s eliminací falešných poplachů.'
        },
        glass: {
          title: 'Detekce tříštění a rozbití skla',
          lead: 'Okamžitá identifikace prasknutí skla a oken pro prevenci vloupání.',
          desc: 'Detekuje náraz i vysokofrekvenční rezonanci dříve, než pachatel vnikne do objektu.'
        },
        drone: {
          title: 'Akustická ochrana perimetru před drony',
          lead: '360° sledování frekvence rotorů dronů pro ochranu věznic a kritické infrastruktury.',
          desc: 'Detekuje nepovolené drony na velkou vzdálenost za každého počasí.'
        },
        scream: {
          title: 'Klasifikace panického křiku a agrese',
          lead: 'Přesná klasifikace panických výkřiků a slovní agrese pro včasnou deeskalaci.',
          desc: '100% v souladu s GDPR bez odposlechu nebo nahrávání řeči.'
        },
        graffiti: {
          title: 'Detekce sprejování a prevence vandalismu',
          lead: 'Rozpozná syčení trysky spreje a míchací kuličku pro ochranu majetku v reálném čase.',
          desc: 'Zastaví vandaly dříve, než dojde k poškození fasád a památek.'
        },
        grinder: {
          title: 'Detekce úhlových brusek a řezných nástrojů',
          lead: 'Detekce řezání kovů a trezorů při pokusech o narušení perimetru.',
          desc: 'Okamžitě zalarmuje zásahovou jednotku při řezání plotů či trezorových skříní.'
        },
        siren: {
          title: 'Rozpoznávání sirén vozidel IZS',
          lead: 'Klasifikace sirén pro dynamickou preferenci vozidel IZS na světelných křižovatkách.',
          desc: 'Zrychluje průjezd záchranných složek městskou dopravou.'
        },
        other: {
          title: 'Zakázková neuronová akustická AI',
          lead: 'Vývoj a trénování modelů na míru pro specifické průmyslové zvuky a anomálie.',
          desc: 'Trénujeme specializované klasifikátory přímo na zvukových vzorcích zákazníka.'
        }
      },
      footer: {
        company: 'Společnost',
        tagline: 'Doplňujeme stávající kamerový systém o digitální akustický sluch.',
        trademark: 'Sound Event Detector je registrovaná ochranná známka společnosti JALUD Embedded s.r.o. (EUIPO č. 018598475)',
        certifications: 'Certifikace ISO 9001:2015 a ISO 27001 · 100% soulad s GDPR',
        legal: 'Všechna práva vyhrazena',
        copyright: '© 2020–2026 JALUD Embedded s.r.o. Všechna práva vyhrazena.'
      },
      home: {
        heroBadge: '🏆 Evropský lídr v akustické AI detekci zvukových událostí · Edge Embedded AI',
        heroTitle: 'Akustická inteligence pro okamžitou reakci na bezpečnostní hrozby',
        heroTitleLine1: 'Kamera vidí.',
        heroTitleLine2: 'SED slyší.',
        heroSubtitle: 'Detekujte střelbu, panický křik, agresi a rozbití skla v reálném čase. Využijte jako samostatný hardware nebo nativní aplikaci do chytrých kamer bez nahrávání konverzací.',
        requestConsultation: 'Požádat o bezplatnou konzultaci',
        viewDetections: 'Prozkoumat schopnosti detekce →',
        videoEyebrow: 'VIDEA A REPORTÁŽE',
        seeInAction: 'Sound Event Detector v praxi',
        watchFieldDemo: 'Reportáže a ukázky testování technologie v reálných podmínkách.',
        videoCtTitle: 'Česká televize (ČT24) - Pořad Bilance ↗',
        videoCtDesc: 'Speciální zvukové detektory v Plzni vyslaly do terénu 18 policejních hlídek jen za měsíc leden po detekci panického křiku a tříštění skla.',
        videoCtTag: '📺 ČT24 Bilance',
        videoGunTitle: 'Detekce střelby a automatické navádění PTZ kamer ↗',
        videoGunDesc: 'Taktická terénní ukázka ukazující, jak SED detekuje ostré výstřely a automaticky natáčí městské kamery na souřadnice střelby.',
        videoGunTag: '💥 Taktická ukázka',
        watchYt: 'Přehrát na YouTube ↗',
        statResponse: '7x rychlejší reakce',
        statResponseDesc: 'Akustická detekce upozorní operátora do <5s, ještě než svědci stihnou vytočit tísňovou linku.',
        statCameras: '0 nových kamer',
        statCamerasDesc: 'Nativní integrace přes ONVIF a VMS pluginy s vaší stávající infrastrukturou Axis, Hanwha, Milestone či Genetec.',
        statPrivacy: '100% ochrana soukromí',
        statPrivacyDesc: 'Žádné kontinuální ukládání zvuku. Neuronové sítě zpracovávají signál výhradně v dočasné paměti RAM na zařízení.',
        acousticAdvantageEyebrow: 'VÝHODY AKUSTICKÉ DETEKCE',
        comprehensiveCoverageEyebrow: 'KOMPLEXNÍ AKUSTICKÉ POKRYTÍ',
        flexibleArchEyebrow: 'FLEXIBILNÍ ARCHITEKTURA',
        consultEyebrow: 'PŘÍMÁ TECHNICKÁ KONZULTACE',
        customerEyebrow: 'OVĚŘENO V OSTRÉM PROVOZU',
        meetTheTeam: 'NÁŠ TÝM A VEDENÍ SPOLEČNOSTI',
        docsResources: 'DOKUMENTACE A TECHNICKÉ ZDROJE',
        cooperationEyebrow: 'PARTNERSTVÍ A SPOLUPRÁCE',
        blindSpotsTitle: 'Kamery mají slepá místa. Zvuk nikoliv.',
        blindSpotsLead: 'Běžné kamerové systémy jsou omezeny úhlem záběru, osvětlením, překážkami a vegetací. SED promění vaše zabezpečení v 360stupňový akustický radar.',
        threatsTitle: 'Odhalte bezpečnostní hrozby dříve, než eskalují',
        threatsSubtitle: 'Pokročilé neuronové sítě trénované na stovkách tisíc reálných akustických vzorků.',
        liveAlertsTitle: 'Živý tok detekcí v reálném čase (Smart City Plzeň)',
        consultTitle: 'Požádejte o bezplatnou technickou konzultaci a posouzení lokality',
        consultDesc: 'Náš inženýrský tým posoudí vaše požadavky a navrhne optimální konfiguraci hardwaru nebo kamerových aplikací.',
        nameLabel: 'Jméno a příjmení *',
        companyLabel: 'Společnost nebo město *',
        emailLabel: 'Pracovní e-mail *',
        facilityLabel: 'Popis objektu a požadavků *',
        facilityPlaceholder: 'Popište svůj objekt a požadavky (např. sklad 2 000 m², IP kamery (Axis, Hanwha, Hikvision), požadavek na detekci křiku a paniky)...',
        namePlaceholder: 'Jan Novák',
        companyPlaceholder: 'např. Město Plzeň, Securitas',
        emailPlaceholder: 'jan.novak@organizace.cz',
        submitConsult: 'Odeslat žádost o konzultaci →',
        consultNotice: 'Poptávky jsou odesílány přímo na sales@jalud-embedded.com. Nezávazná konzultace.',
        consultSuccess: 'Žádost o konzultaci byla úspěšně odeslána! Náš tým se vám ozve do 1 pracovního dne.',
        consultFailed: 'Žádost se nepodařilo odeslat. Napište nám prosím přímo na sales@jalud-embedded.com.'
      },
      solutions: {
        eyebrow: 'MOŽNOSTI NASAZENÍ A SYSTÉMOVÁ ARCHITEKTURA',
        title: 'Tři flexibilní způsoby nasazení akustické inteligence SED',
        subtitle: 'Od samostatných edge IoT senzorů po nativní aplikace přímo v kamerách a centrální enterprise servery.',
        tier1Title: '1. Edge hardware a kamerové aplikace',
        tier1Desc: 'Běží lokálně na platformách Axis ACAP, Hanwha Open Platform nebo dedikovaném hardwaru SED-Box. Zpracování v RAM s nulovým odposlechem.',
        tier2Title: '2. Centrální server a cloudový cluster',
        tier2Desc: 'Sdružuje stovky distribuovaných akustických senzorů napříč celými městy, průmyslovými areály i univerzitními kampusy.',
        tier3Title: '3. Mobilní aplikace SED a okamžitá varování',
        tier3Desc: 'Okamžitá push notifikace s GPS souřadnicemi a spektrogramem zvuku doručená přímo hlídkám městské policie a ostraze.',
        vmsTitle: 'Podporované VMS systémy a bezpečnostní ekosystém',
        vmsSubtitle: 'Hladká plug-and-play integrace s předními světovými platformami přes ONVIF Profile M, REST API a nativní pluginy.',
        downloadSpecs: 'Stáhnout technickou specifikaci architektury (PDF) →'
      },
      aboutUs: {
        eyebrow: 'O SPOLEČNOSTI JALUD EMBEDDED',
        title: 'Průkopníci evropské edge akustické AI',
        subtitle: 'Založeno akustickými inženýry a vědci v Plzni. Naším posláním je zvýšit bezpečí ve veřejném prostoru i na pracovištích při 100% respektování soukromí občanů.',
        teamTitle: 'Vedení a vývojový inženýrský tým',
        storyTitle: 'Naše mise a technologická cesta',
        storyP1: 'Společnost JALUD Embedded s.r.o. vznikla s jasným cílem: přeměnit složitou akustickou analýzu z drahých laboratorních přístrojů na dostupnou, lehkou inteligenci běžící přímo na mikročipech.',
        storyP2: 'Naše neuronové klasifikátory získaly Národní cenu za AI, ocenění Vizionáři a evropskou pečeť Seal of Excellence a chrání bezpečnost občanů v evropských městech.',
        awardsTitle: 'Ocenění, certifikace a mezinárodní standardy',
        iso9001: 'Certifikace systému managementu kvality ISO 9001:2015',
        iso27001: 'Certifikace bezpečnosti informací ISO/IEC 27001',
        gdprCertified: '100% soulad s GDPR potvrzený právní studií a Úřadem pro ochranu osobních údajů',
        ceMark: 'Certifikace CE a FCC pro hardwarové produkty'
      },
      mapPage: {
        eyebrow: 'GLOBÁLNÍ SÍŤ A PARTNEŘI',
        title: 'Mapa distributorů a systémových integrátorů',
        subtitle: 'Prozkoumejte síť našich certifikovaných distributorů, autorizovaných systémových integrátorů a technologických partnerů po celém světě.',
        filterType: 'Filtrovat typ partnera:',
        allPartners: 'Všichni partneři',
        distributorsOnly: 'Pouze distributoři',
        integratorsOnly: 'Pouze integrátoři a partneři',
        regionalZoom: 'Rychlé přiblížení regionu:',
        mapTitle: 'Interaktivní mapa partnerů',
        mapHint: 'Posunujte nebo klikněte na + / - pro detailní přiblížení',
        legendDistributor: 'Modrý špendlík = Hlavní distributor',
        legendIntegrator: 'Oranžový špendlík = Systémový integrátor',
        loadingMap: 'Načítání mapových podkladů a partnerů...',
        directoryTitle: 'Seznam partnerů',
        searchPlaceholder: '🔍 Hledat název firmy, město, e-mail nebo stát...',
        noMatches: 'Nebyli nalezeni žádní partneři',
        noMatchesHint: 'Zkuste upravit vyhledávací dotaz nebo vybrat jiný filtr partnerů.',
        zoomOnMap: 'Zobrazit na mapě',
        ctaTitle: 'Staňte se autorizovaným distributorem nebo integrátorem SED',
        ctaDesc: 'Rozšiřte své bezpečnostní portfolio o přední technologii akustické AI detekce. Nabízíme nativní pluginy pro všechny podporované platformy (Axis, Hanwha, Hikvision...), VMS integrace, bezplatné NFR licence pro testování a marketingovou podporu.',
        ctaButton: 'Požádat o partnerský program →'
      },
      contactPage: {
        subtitle: 'Máte dotazy k řešení Sound Event Detector, integraci kamer nebo partnerské spolupráci? Náš inženýrský a obchodní tým je vám plně k dispozici.',
        placeholder: 'Jak vám můžeme pomoci s akustickou detekcí nebo nasazením systému?',
        inquiryNotice: 'Dotazy jsou odesílány přímo na sales@jalud-embedded.com.',
        businessHours: 'Pracovní doba: Po - Pá, 8:00 - 17:00 SEČ',
        hqTitle: 'Sídlo společnosti JALUD Embedded s.r.o.',
        devTitle: 'Vývojové centrum JALUD Embedded s.r.o.',
        supportTitle: 'Globální podpora a certifikace',
        isoCert: 'ISO 9001 · ISO 27001 · CE certifikovaná kvalita a bezpečnost',
        euTrademark: 'Ochranná známka EU č. 018598475',
        title: 'Spojte se s naším týmem',
        eyebrow: 'SÍDLO A VÝVOJOVÉ CENTRUM',
        headquarters: 'Sídlo společnosti',
        development: 'Výzkum a vývoj',
        globalSupport: 'Globální podpora a shoda',
        name: 'Jméno a příjmení',
        company: 'Společnost / Město',
        email: 'Pracovní e-mail',
        phone: 'Telefonní číslo (nepovinné)',
        facility: 'Popis objektu a požadavků',
        submit: 'Odeslat zprávu →',
        sending: 'Odesílání zprávy...',
        successTitle: 'Zpráva byla úspěšně odeslána!',
        successDesc: 'Děkujeme za kontaktování! Vaše zpráva byla odeslána na sales@jalud-embedded.com. Náš tým se vám brzy ozve.',
        errorTitle: 'Odeslání selhalo',
        errorDesc: 'Zprávu se nepodařilo odeslat. Zkontrolujte prosím připojení nebo nám napište přímo na sales@jalud-embedded.com.'
      },
      radar: {
        liveStream: 'RADAR.ŽIVÝ_TOK',
        location: 'Město Plzeň',
        disclaimer: 'Data jsou zpožděna o 1h a zároveň zvuky eventů jsou simulovány',
        unitsCount: '{count} aktivních jednotek online',
        unitsLabel: 'Aktivní jednotky',
        confidence: 'Spolehlivost',
        forwarded: 'Automatické předání do VMS aktivní',
        justNow: 'Právě teď',
        agoSec: 'před {sec} s',
        agoMin: 'před {min} min',
        events: {
          gunshot: 'Detekce střelby',
          scream: 'Panický křik',
          maleScream: 'Mužský křik',
          childScream: 'Dětský křik',
          aggression: 'Verbální agrese',
          glass: 'Tříštění skla',
          drone: 'Detekce dronu',
          grinder: 'Úhlová bruska',
          siren: 'Siréna vozidla IZS',
          spray: 'Aerosolový sprej',
          other: 'Zvuková událost'
        }
      },
      cookie: {
        text: 'Používáme nezbytné soubory cookie pro analýzu provozu, ochranu formulářů a zajištění spolehlivého zabezpečení.',
        decline: 'Odmítnout volitelné',
        accept: 'Přijmout všechny cookies'
      },
      cooperationTypes: {
        citizen: 'Aktivní občané a města',
        distributor: 'Distributorský program',
        integrator: 'Systémový integrátor',
        customer: 'Zákaznická podpora a SLA',
        investor: 'Investiční partnerství',
        partner: 'Technologický a OEM partner'
      }
    }
  },
  de: {
    name: 'Deutsch',
    dir: 'ltr',
    translations: {
      nav: {
        solutions: 'Lösungen',
        detection: 'Erkennung',
        markets: 'Märkte',
        cooperation: 'Kooperation',
        distributorsMap: 'Distributoren & Partner',
        integratorsMap: 'Distributoren & Partner',
        news: 'Aktuelles',
        latestNews: 'Aktuelles & Presse',
        caseStudies: 'Fallstudien',
        productUpdates: 'Produkt-Updates',
        aboutUs: 'Über uns',
        manuals: 'Handbücher & Datenblätter',
        contact: 'Kontakt',
        getQuote: 'Angebot anfordern',
        privacy: 'Datenschutz',
        privacyPolicy: 'Datenschutz',
        terms: 'AGB',
        codeOfEthics: 'Verhaltenskodex',
        policyIMS: 'IMS-Sicherheitspolitik',
        cookiePolicy: 'Cookie-Richtlinie'
      },
      hero: {
        subtitle: 'Erkennen Sie Schüsse, Schreie, Aggressionen und Glasbruch in Echtzeit. Als eigenständige Edge-Hardware oder native Smart-Kamera-App ohne kontinuierliche Audioaufzeichnung.',
        btnSolutions: 'Lösungen & Architektur erkunden',
        btnContact: 'Kostenlose Beratung anfordern'
      },
      common: {
        learnMore: 'Mehr erfahren →',
        readMore: 'Vollständigen Bericht lesen →',
        contactUs: 'Vertrieb & Technik kontaktieren →',
        applyContact: 'Partner werden →',
        allTypes: 'Alle Kooperationsmodelle ansehen →',
        submitApp: 'Bewerbung absenden →',
        keyBenefits: 'Wichtigste Vorteile der Partnerschaft',
        backToHome: 'Zurück zur Startseite',
        search: 'Suchen...',
        loading: 'Wird geladen...',
        downloadPdf: 'Offizielles PDF herunterladen',
        viewDetails: 'Details anzeigen',
        close: 'Schließen',
        publishedOn: 'Veröffentlicht am',
        allCategories: 'Alle Kategorien',
        hardwareVersion: 'Hardware-Version',
        cameraVersion: 'Kamera-App-Version'
      },
      customers: {
        title: 'Bewährte Praxiseinsätze',
        subtitle: 'Geschätzt von Städten, Polizeibehörden, Krankenhäusern und Infrastrukturbetreibern in ganz Europa.'
      },
      testimonials: {
        title: 'Was Sicherheitsexperten über SED sagen',
        subtitle: 'Direktes Feedback von Bürgermeistern, Sicherheitsleitern von Kliniken und Technologiepartnern.'
      },
      newsSection: {
        title: 'Aktuelle Nachrichten & Presseberichte',
        subtitle: 'Bleiben Sie informiert über technologische Entwicklungen, TV-Beiträge, Auszeichnungen und Projekterfolge.'
      },
      marketTypes: {
        publicSafety: 'Öffentliche Sicherheit & Smart Cities',
        public: 'Öffentliche Sicherheit & Smart Cities',
        healthcare: 'Gesundheitswesen & Kliniken',
        education: 'Bildungseinrichtungen & Campusse',
        industrial: 'Industrie & Kritische Infrastruktur',
        private: 'Gewerbeimmobilien & Einkaufszentren'
      },
      detectionTypes: {
        gunshot: 'Schüsse & Explosionen',
        glass: 'Glasbrucherkennung',
        drone: 'Drohnen- & UAV-Detektion',
        scream: 'Schreie & Aggression',
        graffiti: 'Graffiti-Vandalismus',
        grinder: 'Trennschleifer & Einbruchswerkzeuge',
        siren: 'Fahrzeugsirenen & Alarme',
        other: 'Industrie- & Spezialgeräusche'
      },
      detectionPages: {
        gunshot: {
          title: 'Akustische Schusswaffenerkennung in Echtzeit',
          lead: 'Echtzeit-Erkennung von Schussgeräuschen in <5s mit automatischem PTZ-Kameraschwenk.',
          desc: 'Analysiert Mündungsknall-Druckwellen zuverlässig ohne Fehlalarme.'
        },
        glass: {
          title: 'Akustische Glasbrucherkennung',
          lead: 'Erkennt das Zerbersten von Fenstern und Glasfronten sofort zur Einbruchsprävention.',
          desc: 'Kombinierte Erkennung von Aufschlag und hochfrequenter Glasresonanz.'
        },
        drone: {
          title: 'Akustische Drohnendetektion & Luftraumschutz',
          lead: '360°-Erfassung von Rotorharmonischen zum Schutz kritischer Infrastrukturen und JVA.',
          desc: 'Erkennt unbefugte Drohnen frühzeitig bei jeder Witterung.'
        },
        scream: {
          title: 'Klassifizierung von Schreien und Aggressionen',
          lead: 'Präzise Unterscheidung von Panikschreien und verbaler Aggression zur Deeskalation.',
          desc: '100% DSGVO-konform ohne Sprachaufzeichnung oder Abhören.'
        },
        graffiti: {
          title: 'Graffiti- & Vandalismus-Prävention',
          lead: 'Erkennt Sprühdosenzischen und Mischkugeln zum Schutz öffentlicher Bauten.',
          desc: 'Stoppt Vandalen, bevor Fassaden großflächig beschädigt werden.'
        },
        grinder: {
          title: 'Erkennung von Trennschleifern und Trennwerkzeugen',
          lead: 'Echtzeiterkennung von Metalltrennen an Zäunen und Tresorräumen.',
          desc: 'Löst bei Durchtrennungsversuchen sofort Alarm aus.'
        },
        siren: {
          title: 'Erkennung von Einsatzfahrzeug-Sirenen',
          lead: 'Klassifiziert Sirenen zur intelligenten Ampelpriorisierung für Einsatzkräfte.',
          desc: 'Beschleunigt Rettungseinsätze im städtischen Verkehr.'
        },
        other: {
          title: 'Individuelle akustische KI-Modelle',
          lead: 'Entwicklung maßgeschneiderter Modelle für spezifische Industriegeräusche.',
          desc: 'Wir trainieren neuronale Netze direkt auf kundenspezifische Schallsignaturen.'
        }
      },
      footer: {
        tagline: 'Ergänzung bestehender Videosysteme um ein digitales akustisches Gehör.',
        trademark: 'Sound Event Detector ist eine eingetragene Marke der JALUD Embedded s.r.o. (EUIPO-Nr. 018598475)',
        certifications: 'ISO 9001:2015 & ISO 27001 zertifiziert · 100% DSGVO-konform',
        legal: 'Alle Rechte vorbehalten',
        copyright: '© 2020–2026 JALUD Embedded s.r.o. Alle Rechte vorbehalten.'
      },
      home: {
        heroBadge: '🏆 Europäischer Marktführer für akustische KI-Ereigniserkennung · Edge Embedded AI',
        heroTitle: 'Akustische Intelligenz für sekundenschnelle Sicherheitsreaktion',
        heroTitleLine1: 'Die Kamera sieht.',
        heroTitleLine2: 'SED hört.',
        heroSubtitle: 'Erkennen Sie Schüsse, Schreie, Aggressionen und Glasbruch in Echtzeit. Als eigenständige Edge-Hardware oder native Smart-Kamera-App ohne kontinuierliche Audioaufzeichnung.',
        requestConsultation: 'Kostenlose Beratung anfordern',
        viewDetections: 'Erkennungsfunktionen erkunden →',
        exploreSolutions: 'Lösungen & Architektur',
        videoEyebrow: 'PRAXISDEMONSTRATIONEN',
        seeInAction: 'Sound Event Detector in Aktion erleben',
        watchFieldDemo: 'Erleben Sie Echtzeit-Schallerkennung, automatische PTZ-Kameraführung und städtische Sicherheitsintegration in der Praxis.',
        videoCtTitle: 'Tschechisches Fernsehen (ČT24) - Bericht ↗',
        videoCtDesc: 'Spezielle Schallsensoren in Pilsen führten im Januar zu 18 Polizeieinsätzen nach Erkennung von Panikschreien und Glasbruch.',
        videoCtTag: '📺 ČT24 Bericht',
        videoGunTitle: 'Schusserkennung & automatische PTZ-Kameraausrichtung ↗',
        videoGunDesc: 'Taktische Demonstration: SED erkennt Schüsse und richtet Kameras automatisch auf die Schallkoordinaten aus.',
        videoGunTag: '💥 Taktische Demo',
        watchYt: 'Auf YouTube ansehen ↗',
        watchFieldDemo: 'Video-Demonstrationen aus Polizei- und Praxistests',
        statResponse: '7x schnellere Reaktion',
        statResponseDesc: 'Akustische Warnungen erreichen Leitstellen in <5s, lange bevor Notrufe eingehen.',
        statCameras: '0 neue Kameras erforderlich',
        statCamerasDesc: 'Nahtlose Integration über ONVIF und VMS-Plugins in bestehende Axis-, Hanwha- und Milestone-Systeme.',
        statPrivacy: '100% Datenschutzgarantie',
        statPrivacyDesc: 'Keine permanente Sprachaufzeichnung. Neuronale Netze verarbeiten Schallwellen ausschließlich im flüchtigen RAM.',
        acousticAdvantageEyebrow: 'AKUSTISCHER VORTEIL',
        comprehensiveCoverageEyebrow: 'UMFASSENDE AKUSTISCHE ABDECKUNG',
        flexibleArchEyebrow: 'FLEXIBLE ARCHITEKTUR',
        consultEyebrow: 'DIREKTE TECHNISCHE BERATUNG',
        customerEyebrow: 'BEWÄHRTE PRAXISEINSÄTZE',
        meetTheTeam: 'UNSER FÜHRUNGSTEAM',
        docsResources: 'DOKUMENTATION & RESSOURCEN',
        cooperationEyebrow: 'PARTNERSCHAFT & KOOPERATION',
        blindSpotsTitle: 'Kameras haben tote Winkel. Schall nicht.',
        blindSpotsLead: 'Herkömmliche Videosysteme stoßen bei Dunkelheit, Hindernissen und toten Winkeln an ihre Grenzen. SED macht Ihre Sicherheit zu einem 360-Grad-Akustikradar.',
        threatsTitle: 'Sicherheitsbedrohungen erkennen, bevor sie eskalieren',
        threatsSubtitle: 'Fortschrittliche neuronale Netze, trainiert auf Hunderttausenden realen akustischen Profilen.',
        liveAlertsTitle: 'Live-Ereignisdatenstrom in Echtzeit (Smart City Pilsen)',
        consultTitle: 'Kostenlose technische Beratung & Standortanalyse anfordern',
        consultDesc: 'Unser Engineering-Team prüft Ihre Anforderungen und empfiehlt die optimale Hardware- oder Kamera-App-Konfiguration.',
        nameLabel: 'Vollständiger Name *',
        companyLabel: 'Unternehmen / Stadt *',
        emailLabel: 'Geschäftliche E-Mail *',
        facilityLabel: 'Objektbeschreibung & Anforderungen *',
        facilityPlaceholder: 'Beschreiben Sie Ihre Einrichtung & Anforderungen (z. B. 2.000 m² Lagerhalle, IP-Kameras (Axis, Hanwha, Hikvision), Panikschreierkennung)...',
        namePlaceholder: 'Max Mustermann',
        companyPlaceholder: 'z. B. Stadt Pilsen, Securitas',
        emailPlaceholder: 'max.mustermann@organisation.de',
        submitConsult: 'Beratungsanfrage absenden →',
        consultNotice: 'Anfragen werden direkt an sales@jalud-embedded.com weitergeleitet. Unverbindliche Beratung.',
        consultSuccess: 'Vielen Dank! Ihre Anfrage wurde an sales@jalud-embedded.com übermittelt. Wir melden uns innerhalb von 24 Stunden.',
        consultFailed: 'Anfrage konnte nicht gesendet werden. Bitte schreiben Sie uns an sales@jalud-embedded.com.'
      },
      solutions: {
        eyebrow: 'BEREITSTELLUNGSOPTIONEN & SYSTEMARCHITEKTUR',
        title: 'Drei flexible Wege zur Nutzung der SED Akustik-KI',
        subtitle: 'Vom autarken Edge-Sensor über native Kamera-Apps bis zum zentralen Enterprise-Server.',
        tier1Title: '1. Edge-Hardware & Kamera-Apps',
        tier1Desc: 'Läuft lokal auf Axis ACAP, Hanwha Open Platform oder SED-Box-Hardware. 100% DSGVO-konforme Signalverarbeitung im RAM.',
        tier2Title: '2. Zentraler Server & Cloud-Cluster',
        tier2Desc: 'Bündelt hunderte verteilte akustische Sensoren über ganze Städte, Industrieareale und Universitätsgelände.',
        tier3Title: '3. SED Mobile App & Sofortalarmierung',
        tier3Desc: 'Sekundenschnelle Push-Mitteilungen mit GPS-Koordinaten und Schallspektrogrammen direkt an Sicherheitskräfte vor Ort.',
        vmsTitle: 'Unterstützte Videomanagement-Systeme (VMS)',
        vmsSubtitle: 'Nahtlose Plug-and-Play-Anbindung an führende Plattformen über ONVIF Profile M, REST API und VMS-Plugins.',
        downloadSpecs: 'Architektur-Spezifikation herunterladen (PDF) →'
      },
      aboutUs: {
        eyebrow: 'ÜBER JALUD EMBEDDED',
        title: 'Pioniere der europäischen Edge-Akustik-KI',
        subtitle: 'Gegründet von Akustikingenieuren und KI-Forschern in Pilsen, Tschechische Republik. Unser Ziel: mehr Sicherheit im öffentlichen Raum bei vollem Schutz der Privatsphäre.',
        teamTitle: 'Führungsteam & F&E-Ingenieure',
        storyTitle: 'Unsere Mission & Technologiegeschichte',
        storyP1: 'JALUD Embedded s.r.o. wurde mit dem Ziel gegründet, hochpräzise Schallanalyse aus teuren Speziallaboren in kompakte, praxistaugliche Edge-Hardware zu überführen.',
        storyP2: 'Unsere neuronalen Modelle wurden mit dem Nationalen KI-Preis und dem EU Seal of Excellence ausgezeichnet und schützen tausende Bürger in europäischen Städten.',
        awardsTitle: 'Auszeichnungen, Zertifizierungen & Standards',
        iso9001: 'ISO 9001:2015 Qualitätsmanagement zertifiziert',
        iso27001: 'ISO/IEC 27001 Informationssicherheit zertifiziert',
        gdprCertified: '100% DSGVO-konform — Bestätigt durch Rechtsgutachten & Datenschutzbehörden',
        ceMark: 'CE- und FCC-zertifizierte Hardwareprodukte'
      },
      mapPage: {
        eyebrow: 'GLOBALES NETZWERK & PARTNER',
        title: 'Karte der Distributoren & Systemintegratoren',
        subtitle: 'Entdecken Sie unser weltweites Netzwerk zertifizierter Distributoren, Systemintegratoren und Technologiepartner.',
        filterType: 'Partnertyp filtern:',
        allPartners: 'Alle Partner',
        distributorsOnly: 'Nur Distributoren',
        integratorsOnly: 'Nur Integratoren & Partner',
        regionalZoom: 'Regionaler Zoom:',
        mapTitle: 'Interaktive zoombare Partnerkarte',
        mapHint: 'Scrollen oder +/- klicken zum Zoomen auf Straßenebene',
        legendDistributor: 'Blauer Pin = Master-Distributor',
        legendIntegrator: 'Oranger Pin = Systemintegrator',
        loadingMap: 'Lade hochauflösende Kartendaten...',
        directoryTitle: 'Partnerverzeichnis',
        searchPlaceholder: '🔍 Firma, Stadt, E-Mail oder Land suchen...',
        noMatches: 'Keine Partner gefunden',
        noMatchesHint: 'Bitte Suchbegriff anpassen oder anderen Filter wählen.',
        zoomOnMap: 'Auf Karte zeigen',
        ctaTitle: 'Werden Sie autorisierter SED-Distributor oder Integrator',
        ctaDesc: 'Erweitern Sie Ihr Sicherheitsportfolio um die weltweit führende Edge-KI-Akustikdetektion. Nativ auf allen Plattformen (Axis, Hanwha, Hikvision...), kostenlose NFR-Testlizenzen und Vertriebsunterstützung.',
        ctaButton: 'Partnerprogramm anfragen →'
      },
      contactPage: {
        subtitle: 'Haben Sie Fragen zum Sound Event Detector, zu Kameraintegrationen oder zur Partnerkooperation? Unser Engineering- und Vertriebsteam steht Ihnen gerne zur Verfügung.',
        placeholder: 'Wie können wir Ihnen bei der akustischen Ereigniserkennung oder Bereitstellung helfen?',
        inquiryNotice: 'Anfragen werden direkt an sales@jalud-embedded.com weitergeleitet.',
        businessHours: 'Geschäftszeiten: Mo - Fr, 8:00 - 17:00 MEZ',
        hqTitle: 'JALUD Embedded s.r.o. Hauptsitz',
        devTitle: 'JALUD Embedded s.r.o. Forschung & Entwicklung',
        supportTitle: 'Globaler Support & Compliance',
        isoCert: 'ISO 9001 · ISO 27001 · CE-zertifizierte Qualität und Sicherheit',
        euTrademark: 'EU-Markenzeichen Nr. 018598475',
        title: 'Kontaktieren Sie unser Team',
        eyebrow: 'HAUPTSITZ & ENTWICKLUNGSZENTRUM',
        headquarters: 'Unternehmenssitz',
        development: 'Forschung & Entwicklung',
        globalSupport: 'Globaler Support & Compliance',
        name: 'Vollständiger Name',
        company: 'Unternehmen / Stadt',
        email: 'Geschäftliche E-Mail',
        phone: 'Telefonnummer (optional)',
        facility: 'Objektbeschreibung & Anforderungen',
        submit: 'Nachricht senden →',
        sending: 'Wird gesendet...',
        successTitle: 'Nachricht erfolgreich gesendet!',
        successDesc: 'Vielen Dank! Ihre Anfrage wurde an sales@jalud-embedded.com weitergeleitet. Unser Team wird sich in Kürze bei Ihnen melden.',
        errorTitle: 'Senden fehlgeschlagen',
        errorDesc: 'Nachricht konnte nicht gesendet werden. Bitte schreiben Sie uns direkt an sales@jalud-embedded.com.'
      },
      radar: {
        liveStream: 'RADAR.LIVE_STREAM',
        location: 'Stadt Pilsen',
        disclaimer: 'Daten sind um 1 Stunde verzögert und Ereignistöne werden simuliert.',
        unitsCount: '{count} aktive Einheiten online',
        unitsLabel: 'Aktive Einheiten',
        confidence: 'Konfidenz',
        forwarded: 'VMS-Weiterleitung aktiv',
        justNow: 'Gerade eben',
        agoSec: 'vor {sec}s',
        agoMin: 'vor {min}m',
        events: {
          gunshot: 'Schusserkennung',
          scream: 'Panikschrei',
          maleScream: 'Männerschrei',
          childScream: 'Kinderschrei',
          aggression: 'Verbale Aggression',
          glass: 'Glasbruch',
          drone: 'Drohnenerkennung',
          grinder: 'Trennschleifer',
          siren: 'Fahrzeugsirene',
          spray: 'Spraydosen-Aerosol',
          other: 'Schallereignis'
        }
      },
      cookie: {
        text: 'Wir verwenden Cookies zur Reichweitenanalyse, Formularabsicherung und Optimierung unserer Sicherheitslösungen.',
        decline: 'Optionale ablehnen',
        accept: 'Alle Cookies akzeptieren'
      },
      cooperationTypes: {
        citizen: 'Bürger & Kommunen',
        distributor: 'Distributorenprogramm',
        integrator: 'Systemintegratoren',
        customer: 'Kundensupport & SLA',
        investor: 'Investor Relations',
        partner: 'Technologie- & OEM-Partner'
      }
    }
  },
  es: {
    name: 'Español',
    dir: 'ltr',
    translations: {
      nav: {
        solutions: 'Soluciones',
        detection: 'Detección',
        markets: 'Mercados',
        cooperation: 'Cooperación',
        distributorsMap: 'Mapa de Distribuidores',
        integratorsMap: 'Mapa de Distribuidores',
        news: 'Noticias',
        latestNews: 'Actualidad y Prensa',
        caseStudies: 'Casos de Estudio',
        productUpdates: 'Actualizaciones de Producto',
        aboutUs: 'Sobre Nosotros',
        manuals: 'Manuales y Fichas Técnicas',
        contact: 'Contacto',
        getQuote: 'Solicitar Cotización',
        privacy: 'Política de Privacidad',
        privacyPolicy: 'Política de Privacidad',
        terms: 'Términos y Condiciones',
        codeOfEthics: 'Código de Ética',
        policyIMS: 'Política de Seguridad IMS',
        cookiePolicy: 'Política de Cookies'
      },
      hero: {
        subtitle: 'Detecte disparos, gritos, agresiones y rotura de cristales en tiempo real. En hardware edge dedicado o aplicaciones nativas en cámaras sin transmisión de audio continuo.',
        btnSolutions: 'Explorar soluciones y arquitectura',
        btnContact: 'Solicitar consulta gratuita'
      },
      common: {
        learnMore: 'Más información →',
        readMore: 'Leer informe completo →',
        contactUs: 'Contactar con ventas e ingeniería →',
        applyContact: 'Solicitar colaboración →',
        allTypes: 'Ver todos los modelos de cooperación →',
        submitApp: 'Enviar solicitud →',
        keyBenefits: 'Beneficios clave del programa',
        backToHome: 'Volver al inicio',
        search: 'Buscar...',
        loading: 'Cargando...',
        downloadPdf: 'Descargar PDF oficial',
        viewDetails: 'Ver detalles',
        close: 'Cerrar',
        publishedOn: 'Publicado el',
        allCategories: 'Todas las categorías',
        hardwareVersion: 'Versión de hardware',
        cameraVersion: 'Versión de app de cámara'
      },
      customers: {
        title: 'Despliegues probados en campo',
        subtitle: 'Confiado por municipios, cuerpos policiales, hospitales y operadores de infraestructuras en toda Europa.'
      },
      testimonials: {
        title: 'Lo que dicen los líderes de seguridad sobre SED',
        subtitle: 'Opiniones directas de alcaldes, directores de seguridad hospitalaria y socios tecnológicos.'
      },
      newsSection: {
        title: 'Últimas Noticias y Prensa',
        subtitle: 'Manténgase al día con los avances tecnológicos, reportajes en TV, premios y anuncios de despliegues.'
      },
      marketTypes: {
        publicSafety: 'Seguridad Ciudadana y Smart Cities',
        public: 'Seguridad Ciudadana y Smart Cities',
        healthcare: 'Salud y Hospitales',
        education: 'Educación y Campus',
        industrial: 'Industria e Infraestructuras Críticas',
        private: 'Inmuebles Comerciales y Retail'
      },
      detectionTypes: {
        gunshot: 'Disparos y Explosiones',
        glass: 'Detección de Rotura de Cristales',
        drone: 'Perímetro contra Drones y UAV',
        scream: 'Gritos y Agresión',
        graffiti: 'Vandalismo por Graffiti',
        grinder: 'Amoladoras y Herramientas de Corte',
        siren: 'Sirenas de Vehículos de Emergencia',
        other: 'Sonidos Industriales y a Medida'
      },
      detectionPages: {
        gunshot: {
          title: 'Detección de disparos en tiempo real',
          lead: 'Detección acústica instantánea de disparos en <5s con orientación automática de cámaras PTZ.',
          desc: 'Identifica ondas de choque balísticas eliminando falsas alarmas.'
        },
        glass: {
          title: 'Detección acústica de rotura de cristales',
          lead: 'Identificación instantánea de rotura de lunas y ventanas para prevención de robos.',
          desc: 'Detecta el impacto y la resonancia de alta frecuencia antes de la intrusión.'
        },
        drone: {
          title: 'Protección perimetral acústica contra drones',
          lead: 'Seguimiento 360° de armónicos de motores de drones para prisiones e infraestructuras.',
          desc: 'Detecta drones no autorizados a gran distancia en cualquier clima.'
        },
        scream: {
          title: 'Clasificación de gritos de pánico y agresión',
          lead: 'Clasificación en tiempo real de gritos de auxilio y agresiones verbales para desescalar conflictos.',
          desc: '100% conforme con el GDPR sin grabación de voz humana.'
        },
        graffiti: {
          title: 'Prevención de graffiti y vandalismo',
          lead: 'Detecta el silbido de aerosoles y el agitador metálico para proteger bienes públicos.',
          desc: 'Detiene a los vándalos antes de que causen daños graves.'
        },
        grinder: {
          title: 'Detección de amoladoras y herramientas de corte',
          lead: 'Detección en tiempo real de cortes en vallas perimetrales y cajas fuertes.',
          desc: 'Alerta inmediata ante intentos de robo e intrusión.'
        },
        siren: {
          title: 'Reconocimiento de sirenas de emergencia',
          lead: 'Clasifica sirenas para otorgar prioridad de paso semafórico a vehículos de emergencia.',
          desc: 'Acelera los tiempos de llegada de los servicios de urgencia.'
        },
        other: {
          title: 'IA acústica neuronal a medida',
          lead: 'Entrenamiento de modelos personalizados para sonidos industriales específicos.',
          desc: 'Modelos dedicados entrenados con patrones de audio del cliente.'
        }
      },
      footer: {
        tagline: 'Dotando a las cámaras existentes de audición digital inteligente.',
        trademark: 'Sound Event Detector es una marca registrada de JALUD Embedded s.r.o. (Reg. EUIPO n.º 018598475)',
        certifications: 'Certificaciones ISO 9001:2015 e ISO 27001 · Cumplimiento 100% GDPR',
        legal: 'Todos los derechos reservados',
        copyright: '© 2020–2026 JALUD Embedded s.r.o. Todos los derechos reservados.'
      },
      home: {
        heroBadge: '🏆 Líder europeo en detección acústica de eventos por IA · Edge Embedded AI',
        heroTitle: 'Inteligencia acústica para una respuesta inmediata de seguridad',
        heroTitleLine1: 'La cámara ve.',
        heroTitleLine2: 'SED escucha.',
        heroSubtitle: 'Detecte disparos, gritos, agresiones y rotura de cristales en tiempo real. En hardware edge dedicado o aplicaciones nativas en cámaras sin transmisión de audio continuo.',
        requestConsultation: 'Solicitar consulta gratuita',
        viewDetections: 'Explorar capacidades de detección →',
        exploreSolutions: 'Explorar soluciones y arquitectura',
        videoEyebrow: 'DEMOSTRACIONES EN EL MUNDO REAL',
        seeInAction: 'Vea Sound Event Detector en acción',
        watchFieldDemo: 'Vea la detección acústica en tiempo real, el seguimiento automático con cámaras PTZ y la integración en centros de seguridad.',
        videoCtTitle: 'Televisión Checa (ČT24) - Reportaje ↗',
        videoCtDesc: 'Los detectores acústicos en Pilsen enviaron 18 patrullas policiales en enero tras detectar gritos de pánico y rotura de cristales.',
        videoCtTag: '📺 ČT24 Reportaje',
        videoGunTitle: 'Detección de disparos y seguimiento PTZ automático ↗',
        videoGunDesc: 'Demostración táctica que muestra a SED detectando disparos y orientando cámaras a las coordenadas del sonido.',
        videoGunTag: '💥 Demo Táctica',
        watchYt: 'Ver en YouTube ↗',
        watchFieldDemo: 'Demostraciones en vídeo y pruebas de campo',
        statResponse: 'Respuesta 7x más rápida',
        statResponseDesc: 'Las alertas acústicas notifican a la central en <5s, mucho antes de que los ciudadanos llamen a emergencias.',
        statCameras: '0 cámaras nuevas requeridas',
        statCamerasDesc: 'Integración nativa vía ONVIF y plugins VMS con su infraestructura Axis, Hanwha o Milestone existente.',
        statPrivacy: '100% privacidad garantizada',
        statPrivacyDesc: 'Cero grabación de conversaciones. Las redes neuronales analizan el sonido exclusivamente en memoria RAM volátil.',
        acousticAdvantageEyebrow: 'VENTAJAS ACÚSTICAS',
        comprehensiveCoverageEyebrow: 'COBERTURA ACÚSTICA INTEGRAL',
        flexibleArchEyebrow: 'ARQUITECTURA FLEXIBLE',
        consultEyebrow: 'CONSULTORÍA TÉCNICA DIRECTA',
        customerEyebrow: 'DESPLIEGUES PROBADOS EN CAMPO',
        meetTheTeam: 'NUESTRO EQUIPO DIRECTIVO',
        docsResources: 'DOCUMENTACIÓN Y RECURSOS',
        cooperationEyebrow: 'ALIANZAS Y COLABORACIÓN',
        blindSpotsTitle: 'Las cámaras tienen puntos ciegos. El sonido no.',
        blindSpotsLead: 'La videovigilancia tradicional está limitada por iluminación, obstáculos y ángulos. SED convierte su sistema en un radar acústico de 360 grados.',
        threatsTitle: 'Detecte amenazas de seguridad antes de que escalen',
        threatsSubtitle: 'Redes neuronales avanzadas entrenadas con cientos de miles de patrones acústicos reales.',
        liveAlertsTitle: 'Flujo de eventos en tiempo real (Despliegue Smart City Pilsen)',
        consultTitle: 'Solicite consulta técnica gratuita y evaluación de su instalación',
        consultDesc: 'Nuestro equipo de ingeniería evaluará sus requerimientos y recomendará la configuración óptima.',
        nameLabel: 'Nombre completo *',
        companyLabel: 'Empresa / Municipio *',
        emailLabel: 'Correo corporativo *',
        facilityLabel: 'Descripción de instalaciones y requisitos *',
        facilityPlaceholder: 'Describa sus instalaciones y requisitos (p. ej., almacén de 2.000 m², cámaras IP (Axis, Hanwha, Hikvision), alerta de gritos de pánico)...',
        namePlaceholder: 'Juan Pérez',
        companyPlaceholder: 'p. ej., Ciudad de Pilsen, Securitas',
        emailPlaceholder: 'juan.perez@organizacion.es',
        submitConsult: 'Enviar solicitud de consulta →',
        consultNotice: 'Las solicitudes se envían directamente a sales@jalud-embedded.com. Consulta sin compromiso.',
        consultSuccess: '¡Solicitud enviada con éxito! Nuestro equipo revisará sus requisitos y responderá en 1 día hábil.',
        consultFailed: 'No se pudo enviar la solicitud. Por favor escríbanos a sales@jalud-embedded.com.'
      },
      solutions: {
        eyebrow: 'OPCIONES DE DESPLIEGUE Y ARQUITECTURA',
        title: 'Tres formas flexibles de desplegar la IA acústica SED',
        subtitle: 'Desde sensores edge IoT autónomos hasta aplicaciones integradas en cámaras inteligentes y servidores centrales.',
        tier1Title: '1. Hardware Edge y Apps para Cámaras',
        tier1Desc: 'Ejecución local en Axis ACAP, Hanwha Open Platform o hardware SED-Box. Detección en RAM con 100% de cumplimiento GDPR.',
        tier2Title: '2. Servidor Central y Clúster Cloud',
        tier2Desc: 'Agrega cientos de sensores acústicos distribuidos por ciudades enteras, plantas industriales y campus universitarios.',
        tier3Title: '3. App Móvil SED y Alertas Inmediatas',
        tier3Desc: 'Notificaciones push en <1s con coordenadas GPS y espectrogramas enviadas directamente a patrullas y vigilantes.',
        vmsTitle: 'Sistemas de Gestión de Vídeo (VMS) Compatibles',
        vmsSubtitle: 'Integración plug-and-play con las plataformas líderes mundiales mediante ONVIF Perfil M, REST API y plugins nativos.',
        downloadSpecs: 'Descargar especificación de arquitectura (PDF) →'
      },
      aboutUs: {
        eyebrow: 'SOBRE JALUD EMBEDDED',
        title: 'Pioneros europeos en IA acústica Edge',
        subtitle: 'Fundada por ingenieros acústicos y científicos de IA en Pilsen, República Checa. Comprometidos con crear espacios públicos más seguros protegiendo la privacidad.',
        teamTitle: 'Equipo Directivo e Ingeniería de I+D',
        storyTitle: 'Nuestra Misión y Trayectoria Tecnológica',
        storyP1: 'JALUD Embedded s.r.o. nació con un objetivo: transformar el análisis acústico complejo de costosos laboratorios en inteligencia ligera ejecutada directamente en microprocesadores.',
        storyP2: 'Nuestros algoritmos han ganado el Premio Nacional de IA y el Sello de Excelencia Europeo, protegiendo a miles de personas en ciudades europeas.',
        awardsTitle: 'Premios, Certificaciones y Estándares',
        iso9001: 'Certificado de Gestión de Calidad ISO 9001:2015',
        iso27001: 'Certificado de Seguridad de la Información ISO/IEC 27001',
        gdprCertified: '100% compatible con GDPR — Certificado por dictamen jurídico y autoridades de protección de datos',
        ceMark: 'Productos de hardware certificados CE y FCC'
      },
      mapPage: {
        eyebrow: 'RED GLOBAL Y PARTNERS',
        title: 'Mapa de Distribuidores e Integradores de Sistemas',
        subtitle: 'Explore nuestra red mundial de distribuidores certificados, integradores autorizados y socios tecnológicos.',
        filterType: 'Filtrar tipo de socio:',
        allPartners: 'Todos los socios',
        distributorsOnly: 'Solo Distribuidores',
        integratorsOnly: 'Solo Integradores y Socios',
        regionalZoom: 'Zoom regional:',
        mapTitle: 'Mapa interactivo de distribuidores',
        mapHint: 'Desplácese o pulse + / - para hacer zoom a nivel de calle',
        legendDistributor: 'Marcador Azul = Distribuidor Master',
        legendIntegrator: 'Marcador Ámbar = Integrador de Sistemas',
        loadingMap: 'Cargando mapa de alta precisión...',
        directoryTitle: 'Directorio de Partners',
        searchPlaceholder: '🔍 Buscar empresa, ciudad, email o país...',
        noMatches: 'No se encontraron socios',
        noMatchesHint: 'Pruebe con otro término de búsqueda o seleccione otro filtro.',
        zoomOnMap: 'Ver en el mapa',
        ctaTitle: 'Conviértase en distribuidor o integrador autorizado de SED',
        ctaDesc: 'Amplíe su catálogo de seguridad con la tecnología líder mundial de detección acústica edge por IA. Plugins nativos para todas las plataformas (Axis, Hanwha, Hikvision...), licencias NFR gratuitas para demostraciones y soporte comercial.',
        ctaButton: 'Solicitar unirse al programa →'
      },
      contactPage: {
        subtitle: '¿Tiene preguntas sobre Sound Event Detector, integraciones de cámaras o cooperación con socios? Nuestro equipo de ingeniería y ventas está a su disposición.',
        placeholder: '¿Cómo podemos ayudarle con la detección acústica o la implementación?',
        inquiryNotice: 'Las consultas se envían directamente a sales@jalud-embedded.com.',
        businessHours: 'Horario de atención: Lun - Vie, 8:00 - 17:00 CET',
        hqTitle: 'Sede central de JALUD Embedded s.r.o.',
        devTitle: 'Centro de I+D de JALUD Embedded s.r.o.',
        supportTitle: 'Soporte global y cumplimiento',
        isoCert: 'ISO 9001 · ISO 27001 · Calidad y seguridad certificadas CE',
        euTrademark: 'Marca registrada de la UE n.º 018598475',
        title: 'Póngase en contacto con nuestro equipo',
        eyebrow: 'SEDE CENTRAL Y CENTRO DE I+D',
        headquarters: 'Sede Principal',
        development: 'I+D e Ingeniería',
        globalSupport: 'Soporte Global y Cumplimiento',
        name: 'Nombre completo',
        company: 'Empresa / Municipio',
        email: 'Correo electrónico corporativo',
        phone: 'Teléfono (opcional)',
        facility: 'Descripción de instalaciones y requisitos',
        submit: 'Enviar mensaje →',
        sending: 'Enviando mensaje...',
        successTitle: '¡Mensaje enviado con éxito!',
        successDesc: '¡Gracias por contactarnos! Su mensaje ha sido enviado a sales@jalud-embedded.com. Nuestro equipo se pondrá en contacto pronto.',
        errorTitle: 'Error al enviar mensaje',
        errorDesc: 'No se pudo enviar el mensaje. Por favor escríbanos a sales@jalud-embedded.com.'
      },
      radar: {
        liveStream: 'RADAR.TRANSMISIÓN_VIVO',
        location: 'Ciudad Pilsen',
        disclaimer: 'Los datos tienen un retraso de 1 hora y los sonidos de eventos son simulados.',
        unitsCount: '{count} unidades activas en línea',
        unitsLabel: 'Unidades activas',
        confidence: 'Confianza',
        forwarded: 'Reenvío VMS automático activo',
        justNow: 'Ahora mismo',
        agoSec: 'hace {sec}s',
        agoMin: 'hace {min}m',
        events: {
          gunshot: 'Detección de disparos',
          scream: 'Grito de pánico',
          maleScream: 'Grito masculino',
          childScream: 'Grito infantil',
          aggression: 'Agresión verbal',
          glass: 'Rotura de cristal',
          drone: 'Detección de drones',
          grinder: 'Amoladora angular',
          siren: 'Sirena de emergencia',
          spray: 'Aerosol / Spray',
          other: 'Evento sonoro'
        }
      },
      cookie: {
        text: 'Utilizamos cookies esenciales para analizar el tráfico, proteger formularios y garantizar un rendimiento óptimo de seguridad.',
        decline: 'Rechazar opcionales',
        accept: 'Aceptar todas las cookies'
      },
      cooperationTypes: {
        citizen: 'Ciudadanos y Municipios',
        distributor: 'Programa de Distribuidores',
        integrator: 'Integradores de Sistemas',
        customer: 'Atención al Cliente y SLA',
        investor: 'Relación con Inversores',
        partner: 'Socios Tecnológicos y OEM'
      }
    }
  },
  ar: {
    name: 'العربية',
    dir: 'rtl',
    translations: {
      nav: {
        solutions: 'الحلول',
        detection: 'الكشف',
        markets: 'الأسواق',
        cooperation: 'التعاون',
        distributorsMap: 'الموزعون والشركاء',
        integratorsMap: 'الموزعون والشركاء',
        news: 'الأخبار',
        latestNews: 'آخر الأخبار والصحافة',
        caseStudies: 'دراسات الحالة',
        productUpdates: 'تحديثات المنتجات',
        aboutUs: 'من نحن',
        manuals: 'الأدلة والبيانات الفنية',
        contact: 'اتصل بنا',
        getQuote: 'طلب عرض سعر / تجربة',
        privacy: 'سياسة الخصوصية',
        privacyPolicy: 'سياسة الخصوصية',
        terms: 'الشروط والأحكام',
        codeOfEthics: 'ميثاق الشرف الأخلاقي',
        policyIMS: 'سياسة أمن المعلومات IMS',
        cookiePolicy: 'سياسة ملفات تعريف الارتباط'
      },
      hero: {
        subtitle: 'كشف إطلاق النار والصراخ والعدوان وتكسير الزجاج لحظياً. كأجهزة معالجة طرفية مستقلة أو تطبيقات مدمجة بالكاميرات الذكية دون تسجيل المحادثات.',
        btnSolutions: 'استكشاف الحلول والمعمارية',
        btnContact: 'طلب استشارة فنية مجانية'
      },
      common: {
        learnMore: 'معرفة المزيد ←',
        readMore: 'قراءة التقرير بالكامل ←',
        contactUs: 'تواصل مع فريق المبيعات والهندسة ←',
        applyContact: 'التقديم على الشراكة ←',
        allTypes: 'استكشاف نماذج التعاون ←',
        submitApp: 'إرسال الطلب ←',
        keyBenefits: 'المزايا الرئيسية للشراكة',
        backToHome: 'العودة للرئيسية',
        search: 'بحث...',
        loading: 'جاري التحميل...',
        downloadPdf: 'تحميل ملف PDF الرسمي',
        viewDetails: 'عرض التفاصيل',
        close: 'إغلاق',
        publishedOn: 'نُشر بتاريخ',
        allCategories: 'جميع الفئات',
        hardwareVersion: 'إصدار الجهاز',
        cameraVersion: 'تطبيق الكاميرا'
      },
      customers: {
        title: 'عمليات انتشار ميدانية مثبتة',
        subtitle: 'موثوق به من البلديات وأقسام الشرطة والمستشفيات ومشغلي البنية التحتية في جميع أنحاء أوروبا.'
      },
      testimonials: {
        title: 'ماذا يقول خبراء الأمن عن كاشف الأحداث الصوتية',
        subtitle: 'آراء مباشرة من رؤساء البلديات ومديري أمن المستشفيات والشركاء التكنولوجيين.'
      },
      newsSection: {
        title: 'آخر الأخبار والبيانات الصحفية',
        subtitle: 'ابق على اطلاع بآخر التطورات التكنولوجية والتقارير التلفزيونية والجوائز ومشاريع النشر.'
      },
      marketTypes: {
        publicSafety: 'السلامة العامة والمدن الذكية',
        public: 'السلامة العامة والمدن الذكية',
        healthcare: 'الرعاية الصحية والمستشفيات',
        education: 'التعليم والمجمعات الجامعية',
        industrial: 'الصناعة والبنية التحتية الحيوية',
        private: 'العقارات التجارية والمراكز التجارية'
      },
      detectionTypes: {
        gunshot: 'إطلاق النار والانفجارات',
        glass: 'كشف كسر الزجاج',
        drone: 'كشف الطائرات المسيرة UAV',
        scream: 'الصراخ والعدوان',
        graffiti: 'تخريب الجرافيتي',
        grinder: 'المناشير وقواطع المعادن',
        siren: 'صافرات سيارات الطوارئ',
        other: 'الأصوات الصناعية والمخصصة'
      },
      detectionPages: {
        gunshot: {
          title: 'كشف إطلاق النار في الوقت الحقيقي',
          lead: 'كشف فوري لإطلاق النار في أقل من 5 ثوانٍ مع توجيه الكاميرات المتحركة PTZ وإرسال تنبيهات الطوارئ.',
          desc: 'تحليل موجات الصدمة الصوتية للأسلحة النارية بدقة تامة دون إنذارات كاذبة.'
        },
        glass: {
          title: 'كشف كسر وتطاير الزجاج',
          lead: 'التعرف الصوتي الفوري على تحطم النوافذ والواجهات الزجاجية لمنع الاقتحام.',
          desc: 'رصد ترددات الكسر الحادة قبل دخول المتسللين.'
        },
        drone: {
          title: 'حماية الأجواء من الطائرات المسيرة UAV',
          lead: 'مراقبة صوتية 360 درجة لمحركات الطائرات المسيرة لحماية السجون والمواقع الحيوية.',
          desc: 'كشف المسيرات على مسافات بعيدة في مختلف الظروف الجوية.'
        },
        scream: {
          title: 'تصنيف الصراخ والعدوان اللفظي',
          lead: 'تصنيف فوري لصراخ الذعر والعدوان اللفظي لتهدئة المواقف قبل التصعيد.',
          desc: 'متوافق 100% مع GDPR بدون تسجيل أو استراق المحادثات.'
        },
        graffiti: {
          title: 'مكافحة تخريب الجدران ورش الجرافيتي',
          lead: 'كشف صوت رذاذ البخاخ ورج العلبة لحماية الممتلكات والمعالم العامة.',
          desc: 'إيقاف المخربين قبل تشويه الواجهات.'
        },
        grinder: {
          title: 'كشف مناشير وقواطع المعادن الكهربائية',
          lead: 'رصد عمليات قص الأسوار وخزائن النقود في الوقت الفعلي.',
          desc: 'إنذار فوري عند محاولات الاختراق وقطع الأقفال.'
        },
        siren: {
          title: 'التعرف على صافرات الطوارئ',
          lead: 'تصنيف أصوات الإسعاف والشرطة لإعطاء أولوية المرور في الإشارات الذكية.',
          desc: 'تسريع وصول فرق الإنقاذ عبر التقاطعات المرورية.'
        },
        other: {
          title: 'ذكاء اصطناعي صوتي مخصص للمصانع',
          lead: 'تطوير نماذج ذكاء اصطناعي صوتية مخصصة للأصوات الصناعية غير المعتادة.',
          desc: 'تدريب مخصص على بصمات الصوت الخاصة بالمنشأة.'
        }
      },
      footer: {
        tagline: 'إضافة أذن رقمية للبنية التحتية الأمنية القائمة.',
        trademark: 'Sound Event Detector هي علامة تجارية مسجلة لشركة JALUD Embedded s.r.o. (رقم EUIPO 018598475)',
        certifications: 'معتمد وفق ISO 9001:2015 و ISO 27001 · متوافق 100% مع GDPR',
        legal: 'جميع الحقوق محفوظة',
        copyright: '© 2020–2026 JALUD Embedded s.r.o. جميع الحقوق محفوظة.'
      },
      home: {
        heroBadge: '🏆 الرائد الأوروبي في الكشف الصوتي بالذكاء الاصطناعي · Edge Embedded AI',
        heroTitle: 'الذكاء الصوتي للاستجابة الأمنية الفورية',
        heroTitleLine1: 'الكاميرا ترى.',
        heroTitleLine2: 'SED يسمع.',
        heroSubtitle: 'كشف إطلاق النار والصراخ والعدوان وتكسير الزجاج لحظياً. كأجهزة معالجة طرفية مستقلة أو تطبيقات مدمجة بالكاميرات الذكية دون تسجيل المحادثات.',
        requestConsultation: 'طلب استشارة فنية مجانية',
        viewDetections: 'استكشاف قدرات الكشف ←',
        exploreSolutions: 'استكشاف الحلول والمعمارية',
        videoEyebrow: 'عروض واقعية',
        seeInAction: 'شاهد كاشف الأحداث الصوتية أثناء العمل',
        watchFieldDemo: 'شاهد الكشف الصوتي في الوقت الفعلي والتوجيه التلقائي لكاميرات PTZ في الميدان.',
        videoCtTitle: 'تقرير التلفزيون التشيكي (ČT24) ↗',
        videoCtDesc: 'أرسلت الكواشف الصوتية في بلزن 18 دورية شرطة في شهر يناير بعد رصد صرخات الذعر وكسر الزجاج.',
        videoCtTag: '📺 ČT24 تقرير',
        videoGunTitle: 'كشف إطلاق النار والتوجيه التلقائي لكاميرا PTZ ↗',
        videoGunDesc: 'عرض تكتيكي يوضح كشف طلقات الرصاص وتوجيه الكاميرات نحو إحداثيات الصوت فوراً.',
        videoGunTag: '💥 عرض تكتيكي',
        watchYt: 'مشاهدة على يوتيوب ↗',
        watchFieldDemo: 'عروض الفيديو من الاختبارات الميدانية والشرطية',
        statResponse: 'استجابة أسرع بـ 7 أضعاف',
        statResponseDesc: 'تنبيهات فورية تصل لغرفة العمليات في أقل من 5 ثوانٍ قبل وصول اتصالات الطوارئ.',
        statCameras: 'بدون شراء كاميرات جديدة',
        statCamerasDesc: 'تكامل مباشر مع كاميرات Axis و Hanwha و Milestone عبر ONVIF ومكونات VMS.',
        statPrivacy: 'حماية كاملة للخصوصية 100%',
        statPrivacyDesc: 'لا يتم تسجيل أو تخزين المحادثات الصوتية. تعالج الشبكات العصبية الصوت في الذاكرة المؤقتة فقط.',
        acousticAdvantageEyebrow: 'الميزة الصوتية',
        comprehensiveCoverageEyebrow: 'تغطية صوتية شاملة',
        flexibleArchEyebrow: 'بنية مرنة',
        consultEyebrow: 'استشارة هندسية مباشرة',
        customerEyebrow: 'عمليات نشر ميدانية مثبتة',
        meetTheTeam: 'فريق القيادة',
        docsResources: 'الوثائق والموارد',
        cooperationEyebrow: 'الشراكة والتعاون',
        blindSpotsTitle: 'الكاميرات لها نقاط عمياء، أما الصوت فلا.',
        blindSpotsLead: 'أنظمة المراقبة المرئية محدودة بزوايا الرؤية والظلام والعوائق. يحول كاشف الصوت نظامك إلى رادار صوتي 360 درجة.',
        threatsTitle: 'اكتشف التهديدات الأمنية قبل تفاقمها',
        threatsSubtitle: 'شبكات عصبية متقدمة مدربة على مئات الآلاف من البصمات الصوتية الحقيقية.',
        liveAlertsTitle: 'بث الأحداث الحية في الوقت الحقيقي (مشروع مدينة بلزن الذكية)',
        consultTitle: 'طلب استشارة فنية وتقييم موقع مجاناً',
        consultDesc: 'سيقوم فريقنا الهندسي بتقييم متطلبات منشأتك واقتراح التكوين الأمثل للأجهزة والتطبيقات.',
        nameLabel: 'الاسم الكامل *',
        companyLabel: 'الجهة / المدينة *',
        emailLabel: 'البريد الإلكتروني للعمل *',
        facilityLabel: 'وصف المنشأة والمتطلبات *',
        facilityPlaceholder: 'صف منشأتك ومتطلباتك (مثل مستودع بمساحة 2000 متر مربع، كاميرات IP (Axis، Hanwha، Hikvision)، تنبيه الصراخ والذعر)...',
        namePlaceholder: 'فلان الفلاني',
        companyPlaceholder: 'مثل بلدية بلزن، سكيوريتاس',
        emailPlaceholder: 'name@organization.com',
        submitConsult: 'إرسال طلب الاستشارة ←',
        consultNotice: 'تصل الاستفسارات مباشرة إلى sales@jalud-embedded.com. استشارة بدون التزام.',
        consultSuccess: 'تم إرسال طلب الاستشارة بنجاح! سيتواصل معك فريقنا خلال يوم عمل واحد.',
        consultFailed: 'فشل إرسال الطلب. يرجى مراسلتنا مباشرة على sales@jalud-embedded.com.'
      },
      solutions: {
        eyebrow: 'خيارات النشر والمعمارية الفنية',
        title: 'ثلاث طرق مرنة لنشر ذكاء SED الصوتي',
        subtitle: 'من مستشعرات إنترنت الأشياء الطرفية المستقلة إلى تطبيقات الكاميرات الذكية والخوادم المركزية.',
        tier1Title: '1. أجهزة المعالجة الطرفية وتطبيقات الكاميرات',
        tier1Desc: 'تعمل محلياً على منصات Axis ACAP و Hanwha Open Platform أو أجهزة SED-Box دون نقل الصوت للخارج.',
        tier2Title: '2. الخادم المركزي والسحابة',
        tier2Desc: 'تجميع مئات المستشعرات الصوتية الموزعة عبر المدن والمجمعات الصناعية والجامعات.',
        tier3Title: '3. تطبيق الهاتف والتنبيه الفوري',
        tier3Desc: 'إرسال إشعارات فورية مع إحداثيات GPS والطيف الصوتي مباشرة لدوريات الأمن والشرطة.',
        vmsTitle: 'أنظمة إدارة الفيديو المدعومة (VMS)',
        vmsSubtitle: 'تكامل سلس مع المنصات العالمية عبر ONVIF Profile M و REST API والمكونات البرمجية المدمجة.',
        downloadSpecs: 'تحميل المواصفات الفنية للنظام (PDF) ←'
      },
      aboutUs: {
        eyebrow: 'عن شركة JALUD EMBEDDED',
        title: 'رواد الذكاء الاصطناعي الصوتي الطرفي في أوروبا',
        subtitle: 'تأسست الشركة بواسطة مهندسي صوت وعلماء ذكاء اصطناعي في مدينة بلزن بجمهورية التشيك بهدف حماية الأماكن العامة دون المساس بالخصوصية.',
        teamTitle: 'فريق القيادة والبحث والتطوير الهندسي',
        storyTitle: 'رسالتنا ومسيرتنا التكنولوجية',
        storyP1: 'تأسست شركة JALUD Embedded s.r.o. بهدف تحويل التحليل الصوتي المعقد من المختبرات باهظة الثمن إلى ذكاء برمجي خفيف يعمل مباشرة على المعالجات المدمجة.',
        storyP2: 'حصلت خوارزمياتنا على جائزة الذكاء الاصطناعي الوطنية وختم التميز الأوروبي لحماية آلاف المواطنين في المدن الذكية.',
        awardsTitle: 'الجوائز والشهادات والمعايير الدولية',
        iso9001: 'شهادة إدارة الجودة ISO 9001:2015',
        iso27001: 'شهادة أمن المعلومات ISO/IEC 27001',
        gdprCertified: 'متوافق 100% مع اللائحة العامة لحماية البيانات GDPR وفق استشارة قانونية رسمية',
        ceMark: 'منتجات حاصلة على شهادات CE و FCC الدولية'
      },
      mapPage: {
        eyebrow: 'الشبكة العالمية والشركاء',
        title: 'خريطة الموزعين ومكاملي النظم العالميين',
        subtitle: 'استكشف شبكة موزعينا المعتمدين ومكاملي النظم وشركاء التكنولوجيا حول العالم.',
        filterType: 'تصفية نوع الشريك:',
        allPartners: 'جميع الشركاء',
        distributorsOnly: 'الموزعون فقط',
        integratorsOnly: 'مكاملوا النظم والشركاء فقط',
        regionalZoom: 'تكبير إقليمي:',
        mapTitle: 'خريطة الشركاء التفاعلية',
        mapHint: 'مرر أو اضغط على + / - للتكبير على مستوى الشارع',
        legendDistributor: 'علامة زرقاء = موزع رئيسي',
        legendIntegrator: 'علامة كهرمانية = مكامل نظم',
        loadingMap: 'جاري تحميل الخريطة والبيانات...',
        directoryTitle: 'دليل الشركاء',
        searchPlaceholder: '🔍 ابحث باسم الشركة أو المدينة أو البريد...',
        noMatches: 'لم يتم العثور على شركاء',
        noMatchesHint: 'يرجى تجربة كلمة بحث أخرى أو تغيير خيارات التصفية.',
        zoomOnMap: 'عرض على الخريطة',
        ctaTitle: 'كن موزعاً أو مكامل نظم معتمد لـ SED',
        ctaDesc: 'عزز حلولك الأمنية بأحدث تقنيات الكشف الصوتي بالذكاء الاصطناعي مع دعم فني وتراخيص NFR تجريبية مجانية ودعم تسويقي شامل.',
        ctaButton: 'التقديم للانضمام للبرنامج ←'
      },
      contactPage: {
        subtitle: 'هل لديك أسئلة حول كاشف الأحداث الصوتية أو عمليات تكامل الكاميرا أو التعاون مع الشركاء؟ فريق الهندسة والمبيعات لدينا هنا لمساعدتك.',
        placeholder: 'كيف يمكننا مساعدتك في الكشف عن الأحداث الصوتية أو نشرها؟',
        inquiryNotice: 'يتم إرسال الاستفسارات مباشرة إلى sales@jalud-embedded.com.',
        businessHours: 'ساعات العمل: الإثنين - الجمعة، 8:00 - 17:00 بتوقيت وسط أوروبا',
        hqTitle: 'المقر الرئيسي لشركة JALUD Embedded s.r.o.',
        devTitle: 'مركز البحث والتطوير لشركة JALUD Embedded s.r.o.',
        supportTitle: 'الدعم العالمي والامتثال',
        isoCert: 'ISO 9001 · ISO 27001 · الجودة والأمان المعتمدان من CE',
        euTrademark: 'علامة تجارية مسجلة في الاتحاد الأوروبي رقم 018598475',
        title: 'تواصل مع فريقنا',
        eyebrow: 'المقر الرئيسي ومركز التطوير',
        headquarters: 'المقر الرئيسي',
        development: 'البحث والتطوير الهندسي',
        globalSupport: 'الدعم العالمي والامتثال',
        name: 'الاسم الكامل',
        company: 'الجهة / المنشأة',
        email: 'البريد الإلكتروني للعمل',
        phone: 'رقم الهاتف (اختياري)',
        facility: 'وصف المنشأة والمتطلبات',
        submit: 'إرسال الرسالة ←',
        sending: 'جاري الإرسال...',
        successTitle: 'تم إرسال الرسالة بنجاح!',
        successDesc: 'شكراً لتواصلك! تم إرسال رسالتك إلى sales@jalud-embedded.com وسيتواصل معك فريقنا قريباً.',
        errorTitle: 'فشل إرسال الرسالة',
        errorDesc: 'تعذر إرسال الرسالة. يرجى مراسلتنا مباشرة على sales@jalud-embedded.com.'
      },
      radar: {
        liveStream: 'الرادار.البث_الحي',
        location: 'مدينة بلزن',
        disclaimer: 'البيانات متأخرة لمدة ساعة واحدة مع محاكاة أصوات الأحداث.',
        unitsCount: '{count} وحدة نشطة متصلة',
        unitsLabel: 'الوحدات النشطة',
        confidence: 'نسبة الدقة',
        forwarded: 'التوجيه التلقائي لـ VMS مفعل',
        justNow: 'الآن',
        agoSec: 'منذ {sec} ثانية',
        agoMin: 'منذ {min} دقيقة',
        events: {
          gunshot: 'كشف إطلاق النار',
          scream: 'صرخة ذعر',
          maleScream: 'صرخة رجل',
          childScream: 'صرخة طفل',
          aggression: 'عدوان لفظي',
          glass: 'كسر زجاج',
          drone: 'كشف طائرة مسيرة',
          grinder: 'منشار كهربائي',
          siren: 'صافرة طوارئ',
          spray: 'رذاذ بخاخ',
          other: 'حدث صوتي'
        }
      },
      cookie: {
        text: 'نستخدم ملفات تعريف الارتباط الأساسية لتحليل الحركة وتأمين النماذج وضمان الأداء الأمثل.',
        decline: 'رفض الاختياري',
        accept: 'قبول الكل'
      },
      cooperationTypes: {
        citizen: 'المواطنون النشطون والبلديات',
        distributor: 'برنامج الموزعين',
        integrator: 'مكاملوا النظم',
        customer: 'خدمة العملاء واتفاقيات SLA',
        investor: 'علاقات المستثمرين',
        partner: 'الشركاء التقنيون ومصنعو المعدات الأصلية'
      }
    }
  },
  pt: {
    name: 'Português',
    dir: 'ltr',
    translations: {
      nav: {
        solutions: 'Soluções',
        detection: 'Deteção',
        markets: 'Mercados',
        cooperation: 'Cooperação',
        distributorsMap: 'Mapa de Distribuidores',
        integratorsMap: 'Mapa de Distribuidores',
        news: 'Notícias',
        latestNews: 'Atualidades e Imprensa',
        caseStudies: 'Casos de Estudo',
        productUpdates: 'Atualizações de Produto',
        aboutUs: 'Sobre Nós',
        manuals: 'Manuais e Fichas Técnicas',
        contact: 'Contato',
        getQuote: 'Solicitar Orçamento',
        privacy: 'Política de Privacidade',
        privacyPolicy: 'Política de Privacidade',
        terms: 'Termos e Condições',
        codeOfEthics: 'Código de Ética',
        policyIMS: 'Política de Segurança IMS',
        cookiePolicy: 'Política de Cookies'
      },
      hero: {
        subtitle: 'Detecte tiros, gritos de pânico, agressões e quebra de vidro em tempo real. Em hardware edge dedicado ou aplicativos nativos em câmeras sem gravação de conversas.',
        btnSolutions: 'Explorar soluções e arquitetura',
        btnContact: 'Solicitar consulta gratuita'
      },
      common: {
        learnMore: 'Saiba mais →',
        readMore: 'Ler relatório completo →',
        contactUs: 'Fale com vendas e engenharia →',
        applyContact: 'Candidatar-se a parceiro →',
        allTypes: 'Ver todos os modelos de cooperação →',
        submitApp: 'Enviar inscrição →',
        keyBenefits: 'Principais benefícios da parceria',
        backToHome: 'Voltar ao início',
        search: 'Buscar...',
        loading: 'Carregando...',
        downloadPdf: 'Baixar PDF oficial',
        viewDetails: 'Ver detalhes',
        close: 'Fechar',
        publishedOn: 'Publicado em',
        allCategories: 'Todas as categorias',
        hardwareVersion: 'Versão de hardware',
        cameraVersion: 'Versão de aplicativo de câmera'
      },
      customers: {
        title: 'Implantações comprovadas em campo',
        subtitle: 'Confiado por municípios, polícias, hospitais e operadores de infraestrutura em toda a Europa.'
      },
      testimonials: {
        title: 'O que líderes de segurança dizem sobre o SED',
        subtitle: 'Feedback direto de prefeitos, diretores de segurança hospitalar e parceiros tecnológicos.'
      },
      newsSection: {
        title: 'Últimas Notícias e Imprensa',
        subtitle: 'Fique atualizado sobre inovações tecnológicas, reportagens na TV, prêmios e implantações.'
      },
      marketTypes: {
        publicSafety: 'Segurança Pública e Smart Cities',
        public: 'Segurança Pública e Smart Cities',
        healthcare: 'Saúde e Hospitais',
        education: 'Educação e Campi Universitários',
        industrial: 'Indústria e Infraestrutura Crítica',
        private: 'Propriedades Comerciais e Varejo'
      },
      detectionTypes: {
        gunshot: 'Tiros e Explosões',
        glass: 'Detecção de Quebra de Vidro',
        drone: 'Perímetro contra Drones e UAVs',
        scream: 'Gritos e Agressão',
        graffiti: 'Vandalismo por Grafite',
        grinder: 'Rebarbadora e Corte',
        siren: 'Sirenes de Emergência',
        other: 'Sons Industriais e Personalizados'
      },
      detectionPages: {
        gunshot: {
          title: 'Detecção de disparos em tempo real',
          lead: 'Identificação instantânea de disparos em <5s com orientação automática de câmeras PTZ.',
          desc: 'Analisa ondas de choque de disparos eliminando falsos alarmes.'
        },
        glass: {
          title: 'Detecção acústica de quebra de vidros',
          lead: 'Identificação instantânea de quebra de vidros em janelas e vitrines para prevenção de invasões.',
          desc: 'Detecta o impacto e a ressonância de alta frequência antes da invasão.'
        },
        drone: {
          title: 'Proteção perimetral acústica contra drones',
          lead: 'Rastreamento 360° de harmônicos de rotores de drones para presídios e áreas restritas.',
          desc: 'Detecta drones a longa distância em qualquer condição climática.'
        },
        scream: {
          title: 'Classificação de gritos de pânico e agressão',
          lead: 'Classificação em tempo real de gritos de socorro e agressões verbais para desescalar conflitos.',
          desc: '100% em conformidade com a LGPD/GDPR sem gravação de conversas.'
        },
        graffiti: {
          title: 'Prevenção de vandalismo e pichação',
          lead: 'Detecta o spray de tinta aerossol e esferas misturadoras para proteger o patrimônio.',
          desc: 'Impede vândalos antes que ocorram danos significativos.'
        },
        grinder: {
          title: 'Detecção de rebarbadoras e corte',
          lead: 'Detecção em tempo real de corte de metais em cercas e cofres.',
          desc: 'Alerta imediatamente sobre tentativas de invasão.'
        },
        siren: {
          title: 'Reconhecimento de sirenes de emergência',
          lead: 'Classifica sirenes para priorização semafórica de viaturas e ambulâncias.',
          desc: 'Acelera o tempo de resposta em cruzamentos urbanos.'
        },
        other: {
          title: 'IA acústica sob medida',
          lead: 'Treinamento de modelos dedicados para sons industriais específicos.',
          desc: 'Modelos neurais treinados diretamente com áudios do cliente.'
        }
      },
      footer: {
        tagline: 'Adicionando um ouvido digital à infraestrutura de segurança.',
        trademark: 'Sound Event Detector é uma marca registrada da JALUD Embedded s.r.o. (Reg. EUIPO n.º 018598475)',
        certifications: 'Certificado ISO 9001:2015 e ISO 27001 · 100% em conformidade com LGPD/GDPR',
        legal: 'Todos os direitos reservados',
        copyright: '© 2020–2026 JALUD Embedded s.r.o. Todos os direitos reservados.'
      },
      home: {
        heroBadge: '🏆 Líder europeu em detecção acústica por IA · Edge Embedded AI',
        heroTitle: 'Inteligência acústica para resposta imediata de segurança',
        heroTitleLine1: 'A câmera vê.',
        heroTitleLine2: 'SED ouve.',
        heroSubtitle: 'Detecte tiros, gritos de pânico, agressões e quebra de vidro em tempo real. Em hardware edge dedicado ou aplicativos nativos em câmeras sem gravação de conversas.',
        requestConsultation: 'Solicitar consulta gratuita',
        viewDetections: 'Explorar capacidades de detecção →',
        exploreSolutions: 'Explorar soluções e arquitetura',
        videoEyebrow: 'DEMONSTRAÇÕES PRÁTICAS',
        seeInAction: 'Veja o Sound Event Detector em ação',
        watchFieldDemo: 'Assista à detecção de eventos acústicos em tempo real e ao direcionamento automático de câmeras PTZ.',
        videoCtTitle: 'Televisão Checa (ČT24) - Reportagem ↗',
        videoCtDesc: 'Detectores em Plzeň acionaram 18 patrulhas policiais em janeiro após detectarem gritos de pânico e vidros quebrados.',
        videoCtTag: '📺 ČT24 Reportagem',
        videoGunTitle: 'Detecção de tiros e direcionamento automático PTZ ↗',
        videoGunDesc: 'Demonstração tática mostrando o SED detectando disparos e apontando câmeras para as coordenadas do som.',
        videoGunTag: '💥 Demonstração Tática',
        watchYt: 'Assistir no YouTube ↗',
        watchFieldDemo: 'Demonstrações em vídeo e testes de campo',
        statResponse: 'Resposta 7x mais rápida',
        statResponseDesc: 'Alertas acústicos chegam à central em <5s, muito antes de ligações de emergência.',
        statCameras: '0 novas câmeras necessárias',
        statCamerasDesc: 'Integração nativa via ONVIF e plugins VMS com sua infraestrutura Axis, Hanwha ou Milestone existente.',
        statPrivacy: '100% de privacidade garantida',
        statPrivacyDesc: 'Zero gravação contínua de áudio. Redes neurais processam o sinal exclusivamente em memória RAM volátil.',
        acousticAdvantageEyebrow: 'VANTAGEM ACÚSTICA',
        comprehensiveCoverageEyebrow: 'COBERTURA ACÚSTICA COMPLETA',
        flexibleArchEyebrow: 'ARQUITETURA FLEXÍVEL',
        consultEyebrow: 'CONSULTORIA TÉCNICA DIRETA',
        customerEyebrow: 'IMPLANTAÇÕES COMPROVADAS EM CAMPO',
        meetTheTeam: 'NOSSA EQUIPE DE LIDERANÇA',
        docsResources: 'DOCUMENTAÇÃO E RECURSOS',
        cooperationEyebrow: 'PARCERIAS E COLABORAÇÃO',
        blindSpotsTitle: 'Câmeras têm pontos cegos. O som não.',
        blindSpotsLead: 'A videovigilância tradicional é limitada por ângulos, escuridão e obstáculos. O SED transforma sua segurança em um radar acústico de 360 graus.',
        threatsTitle: 'Detecte ameaças de segurança antes que escalem',
        threatsSubtitle: 'Redes neurais avançadas treinadas com centenas de milhares de perfis acústicos reais.',
        liveAlertsTitle: 'Fluxo de eventos em tempo real (Projeto Smart City Pilsen)',
        consultTitle: 'Solicite consulta técnica gratuita e avaliação de local',
        consultDesc: 'Nossa equipe de engenharia avaliará suas necessidades e recomendará a melhor configuração de hardware e aplicativos.',
        nameLabel: 'Nome completo *',
        companyLabel: 'Empresa / Município *',
        emailLabel: 'E-mail corporativo *',
        facilityLabel: 'Descrição do local e requisitos *',
        facilityPlaceholder: 'Descreva seu local e requisitos (ex.: armazém de 2.000 m², câmeras IP (Axis, Hanwha, Hikvision), alerta de pânico)...',
        namePlaceholder: 'João Silva',
        companyPlaceholder: 'ex.: Município de Plzeň, Securitas',
        emailPlaceholder: 'joao.silva@organizacao.pt',
        submitConsult: 'Enviar solicitação de consulta →',
        consultNotice: 'As solicitações são enviadas diretamente para sales@jalud-embedded.com. Consulta sem compromisso.',
        consultSuccess: 'Solicitação de consulta enviada com sucesso! Nossa equipe entrará em contato em até 1 dia útil.',
        consultFailed: 'Falha ao enviar solicitação. Por favor, escreva diretamente para sales@jalud-embedded.com.'
      },
      solutions: {
        eyebrow: 'OPÇÕES DE IMPLANTAÇÃO E ARQUITETURA',
        title: 'Três formas flexíveis de implantar a inteligência acústica SED',
        subtitle: 'De sensores edge IoT autônomos a aplicativos integrados em câmeras inteligentes e servidores centrais.',
        tier1Title: '1. Hardware Edge e Apps de Câmera',
        tier1Desc: 'Executa localmente em Axis ACAP, Hanwha Open Platform ou hardware SED-Box. Detecção em RAM em total conformidade com a LGPD/GDPR.',
        tier2Title: '2. Servidor Central e Cluster em Nuvem',
        tier2Desc: 'Agrega centenas de sensores acústicos distribuídos por cidades inteiras, plantas industriais e campi universitários.',
        tier3Title: '3. Aplicativo Móvel SED e Alertas Rápidos',
        tier3Desc: 'Notificações push em <1s com coordenadas GPS e espectrogramas enviadas diretamente para patrulhas e seguranças.',
        vmsTitle: 'Sistemas de Gerenciamento de Vídeo (VMS) Suportados',
        vmsSubtitle: 'Integração plug-and-play com as principais plataformas mundiais via ONVIF Perfil M, REST API e plugins nativos.',
        downloadSpecs: 'Baixar especificação de arquitetura (PDF) →'
      },
      aboutUs: {
        eyebrow: 'SOBRE A JALUD EMBEDDED',
        title: 'Pioneiros europeus em IA acústica Edge',
        subtitle: 'Fundada por engenheiros acústicos e cientistas de IA em Pilsen, República Tcheca. Dedicada a tornar espaços públicos e locais de trabalho mais seguros protegendo a privacidade.',
        teamTitle: 'Liderança e Equipe de P&D de Engenharia',
        storyTitle: 'Nossa Missão e Jornada Tecnológica',
        storyP1: 'A JALUD Embedded s.r.o. nasceu com um objetivo claro: transformar a análise acústica avançada de laboratórios caros em inteligência leve executada diretamente em microprocessadores.',
        storyP2: 'Nossos classificadores neurais ganharam o Prêmio Nacional de IA e o Selo de Excelência Europeu, protegendo milhares de cidadãos em cidades inteligentes.',
        awardsTitle: 'Prêmios, Certificações e Padrões',
        iso9001: 'Certificação de Gestão da Qualidade ISO 9001:2015',
        iso27001: 'Certificação de Segurança da Informação ISO/IEC 27001',
        gdprCertified: '100% em conformidade com GDPR/LGPD confirmado por parecer jurídico e órgãos de proteção de dados',
        ceMark: 'Produtos de hardware certificados CE e FCC'
      },
      mapPage: {
        eyebrow: 'REDE GLOBAL E PARCEIROS',
        title: 'Mapa de Distribuidores e Integradores de Sistemas',
        subtitle: 'Explore nossa rede mundial de distribuidores certificados, integradores autorizados e parceiros de tecnologia.',
        filterType: 'Filtrar tipo de parceiro:',
        allPartners: 'Todos os parceiros',
        distributorsOnly: 'Apenas Distribuidores',
        integratorsOnly: 'Apenas Integradores e Parceiros',
        regionalZoom: 'Zoom regional:',
        mapTitle: 'Mapa interativo de parceiros',
        mapHint: 'Role ou clique em + / - para aproximar até o nível da rua',
        legendDistributor: 'Pino Azul = Distribuidor Master',
        legendIntegrator: 'Pino Âmbar = Integrador de Sistemas',
        loadingMap: 'Carregando dados do mapa e parceiros...',
        directoryTitle: 'Diretório de Parceiros',
        searchPlaceholder: '🔍 Buscar empresa, cidade, e-mail ou país...',
        noMatches: 'Nenhum parceiro encontrado',
        noMatchesHint: 'Tente alterar os termos de busca ou selecionar outro filtro.',
        zoomOnMap: 'Ver no mapa',
        ctaTitle: 'Torne-se um distribuidor ou integrador autorizado SED',
        ctaDesc: 'Expanda seu portfólio de segurança com a tecnologia líder mundial em detecção acústica edge por IA. Plugins nativos para todas as plataformas (Axis, Hanwha, Hikvision...), licenças NFR gratuitas para testes e suporte comercial.',
        ctaButton: 'Candidatar-se ao programa →'
      },
      contactPage: {
        subtitle: 'Tem dúvidas sobre o Sound Event Detector, integrações de câmeras ou cooperação com parceiros? Nossa equipe de engenharia e vendas está à disposição.',
        placeholder: 'Como podemos ajudá-lo com a detecção de eventos acústicos ou implantação?',
        inquiryNotice: 'As consultas são enviadas diretamente para sales@jalud-embedded.com.',
        businessHours: 'Horário de atendimento: Seg - Sex, 8:00 - 17:00 CET',
        hqTitle: 'Sede da JALUD Embedded s.r.o.',
        devTitle: 'Centro de P&D da JALUD Embedded s.r.o.',
        supportTitle: 'Suporte global e conformidade',
        isoCert: 'ISO 9001 · ISO 27001 · Qualidade e segurança certificadas CE',
        euTrademark: 'Marca registrada da UE n.º 018598475',
        title: 'Fale com a nossa equipe',
        eyebrow: 'SEDE E CENTRO DE DESENVOLVIMENTO',
        headquarters: 'Sede Principal',
        development: 'P&D e Engenharia',
        globalSupport: 'Suporte Global e Conformidade',
        name: 'Nome completo',
        company: 'Empresa / Município',
        email: 'E-mail corporativo',
        phone: 'Telefone (opcional)',
        facility: 'Descrição do local e requisitos',
        submit: 'Enviar mensagem →',
        sending: 'Enviando mensagem...',
        successTitle: 'Mensagem enviada com sucesso!',
        successDesc: 'Obrigado pelo contato! Sua mensagem foi enviada para sales@jalud-embedded.com. Nossa equipe entrará em contato em breve.',
        errorTitle: 'Falha no envio',
        errorDesc: 'Não foi possível enviar a mensagem. Por favor, escreva diretamente para sales@jalud-embedded.com.'
      },
      radar: {
        liveStream: 'RADAR.TRANSMISSÃO_AO_VIVO',
        location: 'Cidade Pilsen',
        disclaimer: 'Os dados têm um atraso de 1 hora e os sons dos eventos são simulados.',
        unitsCount: '{count} unidades ativas online',
        unitsLabel: 'Unidades ativas',
        confidence: 'Confiança',
        forwarded: 'Encaminhamento VMS automático ativo',
        justNow: 'Agora mesmo',
        agoSec: 'há {sec}s',
        agoMin: 'há {min}m',
        events: {
          gunshot: 'Detecção de disparos',
          scream: 'Grito de pânico',
          maleScream: 'Grito masculino',
          childScream: 'Grito infantil',
          aggression: 'Agressão verbal',
          glass: 'Quebra de vidro',
          drone: 'Detecção de drones',
          grinder: 'Esmerilhadeira angular',
          siren: 'Sirene de emergência',
          spray: 'Spray aerosol',
          other: 'Evento sonoro'
        }
      },
      cookie: {
        text: 'Utilizamos cookies essenciais para analisar o tráfego, proteger formulários e garantir o desempenho ideal de segurança.',
        decline: 'Recusar opcionais',
        accept: 'Aceitar todos os cookies'
      },
      cooperationTypes: {
        citizen: 'Cidadãos Ativos e Municípios',
        distributor: 'Programa de Distribuidores',
        integrator: 'Integradores de Sistemas',
        customer: 'Suporte ao Cliente e SLA',
        investor: 'Relações com Investidores',
        partner: 'Parceiros Tecnológicos e OEM'
      }
    }
  }
};

// Country ISO Code to Supported Locale Mapping
export const countryToLangMap = {
  // Czech & Slovak -> Czech
  'CZ': 'cs',
  'SK': 'cs',

  // German-speaking countries -> German
  'DE': 'de',
  'AT': 'de',
  'CH': 'de',
  'LI': 'de',

  // Spanish-speaking countries -> Spanish
  'ES': 'es',
  'MX': 'es',
  'AR': 'es',
  'CO': 'es',
  'CL': 'es',
  'PE': 'es',
  'VE': 'es',
  'EC': 'es',
  'GT': 'es',
  'CU': 'es',
  'BO': 'es',
  'DO': 'es',
  'HN': 'es',
  'PY': 'es',
  'SV': 'es',
  'NI': 'es',
  'CR': 'es',
  'PA': 'es',
  'UY': 'es',
  'PR': 'es',
  'GQ': 'es',

  // Portuguese-speaking countries -> Portuguese
  'PT': 'pt',
  'BR': 'pt',
  'AO': 'pt',
  'MZ': 'pt',
  'CV': 'pt',
  'GW': 'pt',
  'ST': 'pt',
  'TL': 'pt',

  // Arabic-speaking countries -> Arabic
  'AE': 'ar',
  'SA': 'ar',
  'EG': 'ar',
  'QA': 'ar',
  'KW': 'ar',
  'OM': 'ar',
  'BH': 'ar',
  'IQ': 'ar',
  'JO': 'ar',
  'LB': 'ar',
  'DZ': 'ar',
  'MA': 'ar',
  'TN': 'ar',
  'LY': 'ar',
  'SD': 'ar',
  'YE': 'ar',
  'SY': 'ar',
  'PS': 'ar'
};

// Cookie Helper Functions
export function getCookie(name) {
  try {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]+)'));
    if (match) return decodeURIComponent(match[2]);
  } catch (e) {}
  return null;
}

export function setCookie(name, value, days = 365) {
  try {
    if (typeof document === 'undefined') return;
    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=${maxAge};SameSite=Lax`;
  } catch (e) {}
}

// Browser Language Detection Helper
export function detectBrowserLanguage() {
  try {
    if (typeof navigator === 'undefined') return null;
    const rawLanguages = (navigator.languages && navigator.languages.length) 
      ? navigator.languages 
      : [navigator.language || navigator.userLanguage || ''];
    
    for (const raw of rawLanguages) {
      if (!raw) continue;
      const code = raw.toLowerCase().split('-')[0];
      if (code === 'cs' || code === 'sk') return 'cs';
      if (code === 'de') return 'de';
      if (code === 'es') return 'es';
      if (code === 'pt') return 'pt';
      if (code === 'ar') return 'ar';
      if (code === 'en') return 'en';
    }
  } catch (e) {}
  return null;
}

// Geolocation via IP lookup (Multi-tier redundancy: 1. Server API, 2. ipwho.is, 3. api.country.is, 4. ipapi.co)
export async function fetchCountryFromIP() {
  // Tier 1: Local /api/geoip from our own backend (Zero-CORS, unblockable by adblockers)
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);
    const res = await fetch('/api/geoip', { signal: controller.signal });
    clearTimeout(timeoutId);
    if (res.ok) {
      const data = await res.json();
      if (data && data.country) {
        return data.country.toUpperCase();
      }
    }
  } catch (e) {}

  // Tier 2: ipwho.is
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2200);
    const res = await fetch('https://ipwho.is/', { signal: controller.signal });
    clearTimeout(timeoutId);
    if (res.ok) {
      const data = await res.json();
      if (data && data.country_code) {
        return data.country_code.toUpperCase();
      }
    }
  } catch (e) {}

  // Tier 3: api.country.is
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);
    const res = await fetch('https://api.country.is/', { signal: controller.signal });
    clearTimeout(timeoutId);
    if (res.ok) {
      const data = await res.json();
      if (data && data.country) {
        return data.country.toUpperCase();
      }
    }
  } catch (e) {}

  // Tier 4: ipapi.co
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);
    const res = await fetch('https://ipapi.co/json/', { signal: controller.signal });
    clearTimeout(timeoutId);
    if (res.ok) {
      const data = await res.json();
      if (data && data.country_code) {
        return data.country_code.toUpperCase();
      }
    }
  } catch (e) {}

  return null;
}

let currentLocale = 'en';

export function getLocale() {
  return currentLocale;
}

export function getCurrentLanguage() {
  return currentLocale;
}

export function setLocale(locale, persist = true, isManual = false) {
  if (locales[locale]) {
    currentLocale = locale;
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
      document.documentElement.dir = locales[locale].dir;
    }
    if (persist) {
      try {
        localStorage.setItem('sed_lang', locale);
        if (isManual) {
          localStorage.setItem('sed_manual_lang', '1');
        }
      } catch (e) {}
      setCookie('sed_lang', locale, 365);
      if (isManual) {
        setCookie('sed_manual_lang', '1', 365);
      }
    }
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('sed:languageChange', { detail: { locale, isManual } }));
      }
    } catch (e) {}
  }
}

if (typeof window !== 'undefined') {
  window.setLocale = setLocale;
  window.getLocale = getLocale;
}

// Synchronous Instant Initialization
try {
  const isManual = (getCookie('sed_manual_lang') === '1') || (typeof localStorage !== 'undefined' && localStorage.getItem('sed_manual_lang') === '1');
  const cookieLang = getCookie('sed_lang');
  const storageLang = typeof localStorage !== 'undefined' ? localStorage.getItem('sed_lang') : null;
  const saved = (cookieLang && locales[cookieLang]) ? cookieLang : (storageLang && locales[storageLang] ? storageLang : null);
  
  if (isManual && saved && locales[saved]) {
    currentLocale = saved;
  } else {
    // Check browser languages first for instant matching
    const browserLang = detectBrowserLanguage();
    if (browserLang && locales[browserLang]) {
      currentLocale = browserLang;
    } else {
      currentLocale = saved && locales[saved] ? saved : 'en';
    }
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = currentLocale;
    document.documentElement.dir = locales[currentLocale]?.dir || 'ltr';
  }
} catch (e) {}

/**
 * Robust Automatic Language Detection via IP & Geolocation
 * Runs on every visit unless the user explicitly chose a language via the selector
 */
export async function initVisitorLanguageDetection() {
  try {
    const isManual = (getCookie('sed_manual_lang') === '1') || (typeof localStorage !== 'undefined' && localStorage.getItem('sed_manual_lang') === '1');
    
    // If user explicitly chose a language in the UI, respect their choice
    if (isManual) {
      return;
    }

    // Step 1: Detect country via IP Geolocation
    const country = await fetchCountryFromIP();
    console.log('[SED i18n] Detected visitor country from IP:', country);

    if (country && countryToLangMap[country]) {
      const detectedLang = countryToLangMap[country];
      if (locales[detectedLang]) {
        if (detectedLang !== currentLocale) {
          console.log(`[SED i18n] Auto-switching language to ${detectedLang} based on IP country (${country})`);
          setLocale(detectedLang, true, false);
        }
        return;
      }
    }

    // Step 2: If IP was not in specific country map, check browser preferred languages
    const browserLang = detectBrowserLanguage();
    if (browserLang && locales[browserLang] && browserLang !== currentLocale) {
      console.log(`[SED i18n] Auto-switching language to ${browserLang} based on browser settings`);
      setLocale(browserLang, true, false);
      return;
    }

    // Step 3: Default fallback
    if (!locales[currentLocale]) {
      setLocale('en', true, false);
    }
  } catch (err) {
    console.warn('[SED i18n] Visitor language detection exception:', err);
  }
}

/**
 * Universal Translation Helper: t('category.key', { param: 'value' })
 */
export function t(keyPath, params = {}) {
  const keys = keyPath.split('.');
  let obj = locales[currentLocale]?.translations || locales['en'].translations;

  for (const k of keys) {
    if (obj && obj[k] !== undefined) {
      obj = obj[k];
    } else {
      // Fallback to English
      let fallbackObj = locales['en'].translations;
      for (const fk of keys) {
        if (fallbackObj && fallbackObj[fk] !== undefined) {
          fallbackObj = fallbackObj[fk];
        } else {
          return keyPath;
        }
      }
      obj = fallbackObj;
      break;
    }
  }

  let text = obj || keyPath;

  if (typeof text === 'string' && params && typeof params === 'object') {
    Object.entries(params).forEach(([paramKey, paramVal]) => {
      text = text.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(paramVal));
    });
  }

  return text;
}
